/*
 * live-preview.js — in-house Live Preview (text personalization).
 *
 * Draws the customer's text as an overlay ON THE HERO gallery image, and drives
 * a per-variant dynamic flow:
 *   - typing shows the selected variant's BASE (personalisable) cover;
 *   - the text takes that variant's preset colour;
 *   - clearing the text reverts to the variant's ORIGINAL product image;
 *   - switching variant keeps the text and re-renders it on the new variant's
 *     base + colour ("inherit" across variants).
 *
 * Controls (textarea, font dropdown, alignment, colour) live in the info column.
 * Everything is captured to properties[_lp_preview] in artwork-relative %% so the
 * browser preview matches the print stamping (tools/live-preview/stamp.py).
 *
 * Lazy: no overlay/swap work until the first interaction, so the native gallery
 * stays the LCP element on cold load.
 */
if (!customElements.get('live-preview')) {
  customElements.define(
    'live-preview',
    class LivePreview extends HTMLElement {
      constructor() {
        super();
        this.activated = false;
      }

      connectedCallback() {
        try {
          this.config = JSON.parse(this.dataset.config || '{}');
        } catch (e) {
          this.config = {};
        }
        this.input = this.querySelector('[data-lp-input]');
        this.previewInput = this.querySelector('[data-lp-preview]');
        this.previewBaseInput = this.querySelector('[data-lp-preview-base]');
        this.customInput = this.querySelector('[data-lp-custom]');
        this.linkInput = this.querySelector('[data-lp-link]');
        this.fontSelect = this.querySelector('[data-lp-font]');
        this.alignBtns = this.querySelectorAll('[data-lp-align-btn]');
        // The Command Center's toolbar (2026-09-20): ONE round alignment
        // button that opens a small menu with the three choices; its glyph
        // mirrors the active one. Optional — an older markup has the three
        // buttons in the row and no toggle.
        this.alignToggle = this.querySelector('[data-lp-align-toggle]');
        this.alignMenu = this.querySelector('[data-lp-align-menu]');
        this.sizeDec = this.querySelector('[data-lp-size-dec]');
        this.sizeInc = this.querySelector('[data-lp-size-inc]');
        this.note = this.querySelector('[data-lp-note]'); // «δεν χωράει» — optional
        this.colorControls = this.querySelectorAll('[data-lp-color]');
        this.fallbackStage = this.querySelector('[data-lp-stage]');
        this.fallbackBase = this.querySelector('[data-lp-base]');
        this.jsDebug = document.querySelector('[data-lp-jsdebug]');
        // A standalone instance draws on its OWN stage ([data-lp-stage] +
        // [data-lp-base]) and never on the gallery: the companion card
        // (snippets/live-preview-companion.liquid) is one, drawn inside the
        // add-to-cart notification and on the cart page, where there is no
        // gallery, no product form and no variant picker. Its variant is the
        // hidden [data-lp-variant] inside it, its text is prefilled, and it
        // must be deaf to the PDP's own variant events — the two products
        // share the page but not a colour. Everything else — the fit, the
        // recorded lines, the capture — is the same code, so the order it
        // writes is indistinguishable from one placed on the PDP.
        this.standalone = !!this.config.standalone;
        this.variantInput = this.querySelector('[data-lp-variant]');

        if (!this.input) return;

        const f = this.config.font || {};
        this.variants = this.config.variants || {};
        // Custom Cover Price (2026-10-20, snippets/live-preview.liquid): each
        // colour's own variant ↔ its hidden «Προσωποποιημένο» sibling, with
        // both prices as the shop formats them. Null on every product whose
        // cover is free — and on a standalone instance, whose card picks its
        // own variant (assets/live-preview-companion.js).
        this.cover = null;
        if (!this.standalone && this.config.cover && this.config.cover.pairs) {
          const pairs = {};
          const colourOf = {};
          Object.keys(this.config.cover.pairs).forEach((k) => {
            const p = this.config.cover.pairs[k];
            if (!p || !Number(p.custom) || !Number(k)) return;
            pairs[Number(k)] = p;
            colourOf[Number(p.custom)] = Number(k);
          });
          if (Object.keys(pairs).length) {
            this.cover = { pairs, colourOf, label: this.config.cover.label || '', labelPaid: this.config.cover.labelPaid || '' };
          }
        }
        this.defaultColor = this.config.defaultColor || f.color || '#222';
        this.userColor = null; // set only if the customer picks a colour swatch
        // family -> font-file URL, so the descriptor is self-contained and the
        // cart page can re-render the same preview from properties[_lp_preview].
        this.fontUrls = this.config.fontUrls || {};
        // family -> { sizePct, leading }. Script/handwritten faces behave very
        // differently, so each font carries its own size and line spacing.
        this.fontSpecs = this.config.fontSpecs || {};
        this._variantBase = null; // the base cover URL the text is drawn on now

        // --- Sizing (hybrid autofit + manual stepper) ------------------------
        // autofit: the text grows to the largest size that fits the box (Q1).
        // The +/- buttons let the customer step the size within [min,max] %% of
        // box height (Q2); untouched, the size tracks the autofit fill.
        this.stickyMobile = this.config.stickyMobile !== false;
        this.autofit = this.config.autofit !== false;
        // sizeMin/sizeStep are read as %% OF THE AUTO-FIT, not of the box height
        // — see fitText(). An absolute floor cannot work: the per-font ceilings
        // run 11–49%% of the box since the print geometry was fixed.
        this.sizeMin = Number(this.config.sizeMin) || 50;
        this.sizeMax = Number(this.config.sizeMax) || 100;
        this.sizeStep = Number(this.config.sizeStep) || 10;
        // Where the UNTOUCHED size lands, as %% of the autofit fill. 100 restores
        // the old behaviour (grow until something stops you).
        this.sizeStart = Math.min(100, Math.max(10, Number(this.config.sizeStart) || 85));
        this.sizeTouched = false; // becomes true once the customer uses +/-
        this.userSizePct = null; // the customer's chosen size (%% of box height)
        this._effSizePct = null; // the size actually rendered (goes in the descriptor)

        this.state = {
          text: this.input.value || '',
          font: {
            family: f.family,
            sizePct: f.sizePct || 80,
            leading: f.leading || 1.15,
            weight: f.weight || 700,
            color: this.defaultColor,
            align: f.align || 'center',
            transform: f.transform || 'none',
          },
          variantId: this.config.variantId || null,
        };

        this.overlay = document.createElement('div');
        this.overlay.className = 'lp-overlay';
        this.overlayText = document.createElement('div');
        this.overlayText.className = 'lp-overlay-text';
        this.overlay.appendChild(this.overlayText);

        this.input.addEventListener('focus', this.activate.bind(this), { once: true });
        this.input.addEventListener('input', this.onInput.bind(this));
        if (this.fontSelect) {
          this.fontSelect.addEventListener('change', this.onFontChange.bind(this));
          // Show the selected font in its own typeface in the closed dropdown.
          if (this.fontSelect.value) this.fontSelect.style.fontFamily = `"${this.fontSelect.value}", serif`;
        }
        this.alignBtns.forEach((b) =>
          b.addEventListener('click', () => { this.setAlign(b.getAttribute('value')); this.toggleAlignMenu(false); })
        );
        if (this.alignToggle && this.alignMenu) {
          this.alignToggle.addEventListener('click', () => this.toggleAlignMenu());
          this._alignOutside = (e) => { if (!this.alignMenu.hidden && !this.alignMenu.contains(e.target) && e.target !== this.alignToggle && !this.alignToggle.contains(e.target)) this.toggleAlignMenu(false); };
          this._alignEscape = (e) => { if (e.key === 'Escape' && !this.alignMenu.hidden) { this.toggleAlignMenu(false); this.alignToggle.focus(); } };
          document.addEventListener('click', this._alignOutside, true);
          document.addEventListener('keydown', this._alignEscape);
        }
        this.updateAlignButtons();
        if (this.sizeDec) this.sizeDec.addEventListener('click', () => this.onSizeStep(-1));
        if (this.sizeInc) this.sizeInc.addEventListener('click', () => this.onSizeStep(1));
        this.colorControls.forEach((el) => el.addEventListener('change', this.onColorChange.bind(this)));

        if (!this.standalone && window.subscribe && window.PUB_SUB_EVENTS) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
        this._onResize = this.reflow.bind(this);
        window.addEventListener('resize', this._onResize);

        // Add-to-cart: the last chance to stop a STALE capture travelling with
        // the order. It deliberately does not wait for one in flight — an upload
        // that delays the sale is worth less than no upload at all, and the
        // descriptor alone already produces a printable order.
        var formId = this.previewInput && this.previewInput.getAttribute('form');
        var lpForm = formId ? document.getElementById(formId) : this.closest('form');
        if (lpForm) {
          lpForm.addEventListener('submit', this.sealCapture.bind(this), true);

          // The guarantee is "the right image or none", and the 2.5s debounce
          // decides how often it lands on "none": a customer who types and
          // presses Add within that window gets no image at all. Correct, but a
          // wasted capture.
          //
          // So capture EARLY on the two moments that mean "I have finished",
          // long before submit. Leaving the field is the strongest signal a
          // customer gives; pointerdown on the button precedes the click, which
          // precedes submit — a few hundred milliseconds, spent while the finger
          // is still travelling. Neither blocks anything: if the upload has not
          // landed by submit, sealCapture() still drops it and the order still
          // prints from the descriptor.
          var addBtn = lpForm.querySelector('[name="add"]');
          if (addBtn) {
            addBtn.addEventListener('pointerdown', this.captureNow.bind(this), { passive: true });
          }

          // Custom Cover Price: the variant that goes to the cart is decided
          // HERE, from the field as it is at the tap — text ⇒ the sibling, no
          // text ⇒ the colour's own variant — whatever the page did before.
          // Twice on purpose: the click runs before the submit in every
          // browser, and the capture-phase submit runs before product-form.js
          // reads the form wherever the platform orders target listeners by
          // phase. The sticky bar on the phone forwards a click to this button.
          if (this.cover) {
            lpForm.addEventListener('submit', () => this.syncCover('submit'), true);
            if (addBtn) addBtn.addEventListener('click', () => this.syncCover('submit'), true);
          }
        }

        // A colour picked in Dawn's picker: before Dawn reads the checked
        // values (its listener is on <variant-selects>, in the bubble phase),
        // set the hidden «Εξώφυλλο» for THAT colour. A colour with no sibling
        // (added after the sync) would otherwise be asked for as
        // «colour / Προσωποποιημένο», a variant that does not exist, and Dawn
        // would answer «μη διαθέσιμο» with no event to repair it.
        if (this.cover) {
          this._onPickColour = (e) => this.preparePickedColour(e);
          this._pickHost = this.closest('product-info') || document;
          this._pickHost.addEventListener('change', this._onPickColour, true);
        }
        this.input.addEventListener('blur', this.captureNow.bind(this));

        // One repair, on request. The health monitor is a pure observer and must
        // stay one, so it does not call in here — it asks, and this decides.
        // Most of what it sees is transient: a hero still decoding, a layout
        // that has not settled, a deploy swapping assets underneath a page that
        // was already open. Re-mounting and re-rendering costs a frame and turns
        // most of those into nothing at all, which is far better for the
        // customer than being told the shop is broken.
        this.addEventListener('lp:recover', () => {
          if (!this.activated) return;
          try {
            this.mountOverlay();
            this.render();
          } catch (e) { /* a failed repair must never be worse than none */ }
        });

        // The browser restores a textarea on back/forward navigation — and on
        // a bfcache restore — WITHOUT firing `input`, and it does so after this
        // element had already read the field. `state.text` said '' while the
        // field showed the customer's dedication; their first focus then drew
        // an EMPTY cover and wrote no descriptor, so the order carried words
        // nobody had previewed. That is issue #20 («descriptor-empty,
        // text-not-drawn»), reproduced on the live PDP with cart → back on both
        // desktop and mobile. `pageshow` fires after every restoration in
        // Chrome; WebKit was seen restoring the field only AFTER it (the same
        // flow, driven under its engine), so look once more a beat later. The
        // field's own focus re-reads it in any case — see activate().
        this._onPageShow = () => {
          const sync = () => {
            try { this.syncFromInput(); } catch (e) { /* never break the page */ }
            // a restored page can bring back a form whose variant no longer
            // matches the field (Firefox restores hidden inputs too)
            try { this.syncCover('pageshow'); } catch (e) { /* never break the page */ }
          };
          sync();
          setTimeout(sync, 300);
        };
        window.addEventListener('pageshow', this._onPageShow);

        // A standalone card arrives with its text already in the field and
        // nobody about to focus it: draw it now. A beat later, so the element
        // that the notification just injected has been laid out and the stage
        // image knows its size — syncFromInput() activates only on a non-empty
        // field, so an empty card (a name not yet known) stays a blank cover.
        if (this.standalone) {
          setTimeout(() => { try { this.syncFromInput(); } catch (e) { /* never break the page */ } }, 0);
        }

        // The names a suggestion in the «Προστέθηκε στο καλάθι σου» panel drew on THIS product's cover — see
        // takeHandoff().
        if (!this.standalone) this.takeHandoff();

        // An empty field on a page opened at the sibling (a shared or reloaded
        // ?variant= link) goes back to the colour's own variant and price.
        if (this.cover) {
          try { this.syncCover('init'); } catch (e) { /* never break the page */ }
        }
      }

      disconnectedCallback() {
        if (this._alignOutside) document.removeEventListener('click', this._alignOutside, true);
        if (this._alignEscape) document.removeEventListener('keydown', this._alignEscape);
        if (this._onPickColour && this._pickHost) this._pickHost.removeEventListener('change', this._onPickColour, true);
        if (this.unsubscribe) this.unsubscribe();
        if (this._observer) this._observer.disconnect();
        if (this._bodyObserver) this._bodyObserver.disconnect();
        if (this._sizeObserver) this._sizeObserver.disconnect();
        if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
        if (this._onMiniScroll) window.removeEventListener('scroll', this._onMiniScroll);
        clearTimeout(this._miniTail);
        if (this._onViewport && window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this._onViewport);
          window.visualViewport.removeEventListener('scroll', this._onViewport);
        }
        if (this.mini && this.mini.parentElement) this.mini.parentElement.removeChild(this.mini);
        window.removeEventListener('resize', this._onResize);
      }

      // --- Overlay placement -------------------------------------------------

      findHeroImage() {
        if (this.standalone) return null; // the stage inside the element, always
        const sel = this.config.heroSelector || 'media-gallery';
        const root = document.querySelector(sel);
        if (!root) return null;
        return root.querySelector('.product__media img') || root.querySelector('img');
      }

      mountOverlay() {
        const img = this.findHeroImage();
        let host;
        if (img) {
          host = img.closest('.product__media') || img.parentElement;
          this.heroImg = img;
          // Remember what the gallery showed for this variant so we can revert.
          this.heroOriginalSrc = img.getAttribute('src');
          this.heroOriginalSrcset = img.getAttribute('srcset');
          if (this.fallbackStage) this.fallbackStage.hidden = true;
        } else if (this.fallbackStage) {
          this.fallbackStage.hidden = false;
          this.heroImg = this.fallbackBase;
          this.heroOriginalSrc = this.fallbackBase ? this.fallbackBase.getAttribute('src') : null;
          this.heroOriginalSrcset = this.fallbackBase ? this.fallbackBase.getAttribute('srcset') : null;
          host = this.fallbackStage;
        } else {
          return;
        }
        const cs = getComputedStyle(host);
        if (cs.position === 'static') host.style.position = 'relative';
        if (this.overlay.parentElement !== host) host.appendChild(this.overlay);
        // Re-fit once the (possibly not-yet-loaded) hero image knows its size.
        if (this.heroImg && !this.heroImg.complete) {
          this.heroImg.addEventListener('load', () => this.reflow(), { once: true });
        }
        this.positionOverlay();
        this.watchOverlaySize();
      }

      // The box can lose its size without anything here being told: Dawn's
      // desktop thumbnail layouts hide every media item but `.is-active`
      // (display:none), a variant switch re-renders the gallery, a hero decodes
      // late, a phone rotates. A ResizeObserver sees all of them, so a box that
      // comes back from 0×0 is re-fitted and re-recorded the moment it has a
      // size again — instead of waiting for the next keystroke, which on the
      // live PDP was drawing 16px text across the whole cover in the meantime.
      // Notifications are delivered after layout, never synchronously, and
      // placeBox() writes the same size it reads, so this cannot loop.
      watchOverlaySize() {
        if (this._sizeObserver || !window.ResizeObserver || !this.overlay) return;
        this._sizeObserver = new ResizeObserver((entries) => {
          const r = entries[entries.length - 1].contentRect;
          const key = Math.round(r.width) + 'x' + Math.round(r.height);
          if (key === this._sizeKey) return;
          this._sizeKey = key;
          if (!this.activated || this._reflowing) return;
          this._reflowing = true;
          try { this.reflow(); } catch (e) { /* never break the page */ } finally { this._reflowing = false; }
        });
        this._sizeObserver.observe(this.overlay);
      }

      // Put the cover in front of the customer before drawing on it.
      //
      // Dawn's desktop thumbnail layouts show ONE media item — `.is-active` —
      // and `display:none` the rest. A customer who browsed the inside pages
      // and then typed was therefore drawing into a hidden slide: no preview
      // anywhere, a box with no size, no fit, no recorded lines, and the
      // `overlay-collapsed` fault behind issues #13/#15/#16/#18 — 35% of the
      // desktop customers who engaged, measured in GA4 over 19 days. Reproduced
      // on the live PDP of every product it was reported on.
      //
      // The switch is done by hand rather than through media-gallery's own
      // setActiveMedia(), because that also smooth-scrolls the PAGE to the media
      // whenever it sits above the viewport — and the customer is typing. The
      // mobile slider is only ever moved sideways, for the same reason.
      showCoverSlide() {
        const li = this.heroImg && this.heroImg.closest('.product__media-item');
        if (!li || !li.parentElement) return false;
        const viewer = li.parentElement;
        let changed = false;
        if (!li.classList.contains('is-active')) {
          viewer.querySelectorAll('.product__media-item.is-active').forEach((x) => x.classList.remove('is-active'));
          li.classList.add('is-active');
          const gallery = document.querySelector(this.config.heroSelector || 'media-gallery');
          const thumb = gallery && li.dataset.mediaId
            ? gallery.querySelector('[data-target="' + li.dataset.mediaId + '"]') : null;
          if (thumb && gallery && typeof gallery.setActiveThumbnail === 'function') {
            try { gallery.setActiveThumbnail(thumb); } catch (e) { /* theirs, not ours */ }
          }
          changed = true;
        }
        if (viewer.scrollWidth > viewer.clientWidth + 1 && Math.abs(viewer.scrollLeft - li.offsetLeft) > 2) {
          try { viewer.scrollTo({ left: li.offsetLeft }); } catch (e) { viewer.scrollLeft = li.offsetLeft; }
          changed = true;
        }
        return changed;
      }

      // Take the field's value as the truth, however it got there — typed,
      // restored by the browser, or filled by something else.
      syncFromInput() {
        if (!this.input) return;
        const v = this.input.value || '';
        // Not yet activated: a field with text in it is a restored field
        // (nothing else fills it before the first interaction), and it wants
        // drawing whether or not connectedCallback() already saw the value —
        // WebKit restores BEFORE the element upgrades, Chrome after.
        if (!this.activated) {
          if (v.trim()) this.activate();
          return;
        }
        if (v === (this.state.text || '')) return;
        this.state.text = v;
        this.renderForUser();
      }

      // A tile «με τα ονόματά σας» in the «Προστέθηκε στο καλάθι σου» panel drew the customer's names on
      // THIS product's cover; a tap on its photo or name lands here, and an empty field took back what the
      // tile had shown (Stelios, 08/10/2026). assets/cart-drawer.js (handOver) leaves { p: handle, t: words,
      // at: ms } in this tab's sessionStorage — never the URL. Read ONCE, by the first product page that
      // loads, and removed whatever it says, so a personal name does not linger; used only on its own
      // product, within 30 minutes, and never over words already in the field. Drawn the way a restored
      // field is — the value, then syncFromInput() — so the cover, the descriptor and the capture are the
      // ones typing produces.
      takeHandoff() {
        let h = null;
        try {
          const raw = window.sessionStorage.getItem('mb-lp-handoff');
          if (!raw) return;
          window.sessionStorage.removeItem('mb-lp-handoff');
          h = JSON.parse(raw);
        } catch (e) {
          return;
        }
        if (!h || typeof h.t !== 'string' || !h.t.trim()) return;
        if (!this.config.product || h.p !== this.config.product) return;
        if (!(Date.now() - Number(h.at) < 30 * 60 * 1000)) return;
        if ((this.input.value || '').trim()) return;
        const max = Number(this.input.getAttribute('maxlength')) || 0;
        this.input.value = max > 0 ? h.t.slice(0, max) : h.t;
        // a beat later, as for a standalone card: laid out, so the cover knows its size
        setTimeout(() => { try { this.syncFromInput(); } catch (e) { /* never break the page */ } }, 0);
      }

      // Position the overlay over the ACTUAL rendered image rectangle (accounting
      // for object-fit letterboxing), not the gallery container — so the box lands
      // exactly where the box-tool placed it, at any container size or on mobile.
      positionOverlay() {
        this.placeBox(this.heroImg, this.overlay);
      }

      // Same math for the hero and for the pinned mobile copy: the box is a %% of
      // the IMAGE, so any rendering of the same artwork lands in the same place.
      placeBox(img, overlay) {
        const box = this.config.box || { x: 10, y: 40, w: 80, h: 15 };
        const host = overlay ? overlay.parentElement : null;
        if (!overlay) return;
        const o = overlay.style;
        if (!img || !host || !img.naturalWidth || !img.naturalHeight) {
          // Fall back to % of the container until the image size is known.
          o.left = box.x + '%';
          o.top = box.y + '%';
          o.width = box.w + '%';
          o.height = box.h + '%';
          return;
        }
        const ir = img.getBoundingClientRect();
        const hr = host.getBoundingClientRect();
        if (ir.width <= 0 || ir.height <= 0) {
          // A loaded image with no rectangle is a hidden slide. Leave the box
          // roughly placed rather than untouched: untouched, Dawn's own
          // `.media > *` rule sizes it to the whole cover, and that is what the
          // customer got the moment the slide came back.
          o.left = box.x + '%';
          o.top = box.y + '%';
          o.width = box.w + '%';
          o.height = box.h + '%';
          return;
        }

        const fit = getComputedStyle(img).objectFit || 'contain';
        let cw;
        let ch;
        if (fit === 'cover') {
          const s = Math.max(ir.width / img.naturalWidth, ir.height / img.naturalHeight);
          cw = img.naturalWidth * s;
          ch = img.naturalHeight * s;
        } else if (fit === 'fill' || fit === 'none') {
          cw = ir.width;
          ch = ir.height;
        } else {
          // contain (Dawn default): image scaled to fit, centred, letterboxed.
          const s = Math.min(ir.width / img.naturalWidth, ir.height / img.naturalHeight);
          cw = img.naturalWidth * s;
          ch = img.naturalHeight * s;
        }
        const contentLeft = ir.left + (ir.width - cw) / 2 - hr.left;
        const contentTop = ir.top + (ir.height - ch) / 2 - hr.top;

        o.left = contentLeft + (cw * box.x) / 100 + 'px';
        o.top = contentTop + (ch * box.y) / 100 + 'px';
        o.width = (cw * box.w) / 100 + 'px';
        o.height = (ch * box.h) / 100 + 'px';
      }

      // --- Interaction -------------------------------------------------------

      activate() {
        if (this.activated) return;
        this.activated = true;
        // Whatever the field holds NOW is the text — see syncFromInput().
        this.state.text = this.input.value || '';
        this.warmBases();
        this.loadFont(this.state.font.family);
        this.mountOverlay();
        this.observeGallery();
        this.renderForUser();
      }

      // Dawn re-renders the gallery (on variant change, image load, etc.) and can
      // detach/cover our overlay or reset the swapped image. Watch for that and
      // re-mount the overlay on top + re-apply the base-cover swap.
      observeGallery() {
        if (this.standalone) return; // no gallery re-renders under a stage
        const root = document.querySelector(this.config.heroSelector || 'media-gallery');
        if (!root || this._observer) return;
        this._observer = new MutationObserver(() => {
          if (!this.activated || this._remounting) return;
          const img = this.findHeroImage();
          if (!img) return;
          // Re-mount only when the gallery replaced the hero or dropped our overlay
          // (avoids looping on our own text/style mutations). Synchronous + guarded.
          if (this.heroImg !== img || !this.overlay.isConnected) {
            this._remounting = true;
            try {
              this.mountOverlay();
              this.render();
            } finally {
              this._remounting = false;
            }
          }
        });
        this._observer.observe(root, { childList: true, subtree: true });
      }

      loadFont(fam) {
        if (fam && document.fonts && document.fonts.load) {
          document.fonts.load(`${this.state.font.weight} 40px "${fam}"`).then(() => this.render()).catch(() => {});
        }
      }

      onInput() {
        this.activate();
        this.state.text = this.input.value;
        // Only a keystroke may be refused (see refuseInput()); a resize or a
        // late cover that leaves the same text too big is clamped, not undone.
        this._fromInput = true;
        this._userAction = true;
        try { this._caret = this.input.selectionStart; } catch (e) { this._caret = null; }
        try { this.render(); } finally { this._fromInput = false; this._userAction = false; }
      }

      // render() on the customer's OWN action — the gallery may be brought back
      // to the cover for those. A late webfont, a repair, a variant event or a
      // hero load also render, and must not move a gallery the customer has
      // since clicked away from.
      renderForUser() {
        this._userAction = true;
        try { this.render(); } finally { this._userAction = false; }
      }

      onFontChange(e) {
        const fam = e.target.value;
        this.state.font.family = fam;
        // Each font has its OWN size + leading — apply them and re-fit from that
        // baseline (a stepped size from the previous font means nothing here).
        const spec = this.fontSpecs[fam];
        if (spec) {
          if (spec.sizePct) this.state.font.sizePct = Number(spec.sizePct);
          if (spec.leading) this.state.font.leading = Number(spec.leading);
          this.sizeTouched = false;
          this.userSizePct = null;
        }
        // Reflect the choice in the closed dropdown (font-preview).
        if (this.fontSelect) this.fontSelect.style.fontFamily = `"${fam}", serif`;
        this.activate();
        this.loadFont(fam);
        this.renderForUser();
      }

      // Alignment is an icon-button group; highlight the active one.
      setAlign(value) {
        if (!value) return;
        this.state.font.align = value;
        this.activate();
        this.updateAlignButtons();
        this.renderForUser();
      }

      updateAlignButtons() {
        this.alignBtns.forEach((b) =>
          b.setAttribute('aria-pressed', b.getAttribute('value') === this.state.font.align ? 'true' : 'false')
        );
        // the round button shows the active choice's own icon
        if (this.alignToggle) {
          const active = Array.from(this.alignBtns).find((b) => b.getAttribute('value') === this.state.font.align);
          if (active) this.alignToggle.innerHTML = active.innerHTML;
        }
      }

      // The alignment menu under the round button: open on tap, closed by a
      // choice, a tap outside or Escape.
      toggleAlignMenu(force) {
        if (!this.alignToggle || !this.alignMenu) return;
        const show = typeof force === 'boolean' ? force : this.alignMenu.hidden;
        this.alignMenu.hidden = !show;
        this.alignToggle.setAttribute('aria-expanded', show ? 'true' : 'false');
      }

      // +/- size stepper: step the chosen size within [min,max] and switch off
      // autofit-tracking (the customer is now driving the size manually).
      onSizeStep(dir) {
        this.activate();
        this.sizeTouched = true;
        const fill = this._fillPct != null ? this._fillPct : this.state.font.sizePct || 80;
        const floor = this._floorPct != null ? this._floorPct : fill / 2;
        const step = this._stepPct != null ? this._stepPct : Math.max(fill / 10, 0.5);
        const cur = this.userSizePct != null ? this.userSizePct
          : (this._effSizePct != null ? this._effSizePct : fill);
        let next = cur + dir * step;
        next = Math.min(fill, Math.max(floor, next));
        this.userSizePct = next;
        this.renderForUser();
      }

      // Customer-picked colour overrides the per-variant preset.
      onColorChange(e) {
        this.userColor = e.target.value;
        this.renderForUser();
      }

      onVariantChange(payload) {
        const variant = payload && payload.data && payload.data.variant;
        if (variant) this.state.variantId = this.colourOf(variant.id);
        if (this.activated) {
          // Dawn re-rendered the gallery — re-find the hero, then re-apply.
          this.mountOverlay();
          this.render();
        }
        // A new colour: its own pair, its own price (render() already did this
        // when the field had been touched; an untouched page did not render).
        if (this.cover) this.syncCover('variant');
      }

      // A standalone instance's colour swatches call this: the variant goes
      // into the element's own hidden input (what currentVariantId() reads
      // first), then the cover and colour follow exactly as a PDP variant
      // change would — including a blank field, where only the base swaps.
      setVariant(id) {
        const vid = Number(id);
        if (!vid) return;
        if (this.variantInput) this.variantInput.value = String(vid);
        this.state.variantId = vid;
        if (!this.activated) {
          // Nothing drawn yet: show that variant's blank cover on the stage.
          const v = this.variants[vid] || {};
          const base = v.base || this.config.baseImage;
          if (this.fallbackBase && base && this.fallbackBase.getAttribute('src') !== base) {
            this.fallbackBase.setAttribute('src', base);
          }
          return;
        }
        this.mountOverlay();
        this.renderForUser();
      }

      reflow() {
        if (!this.activated) return;
        // Dawn re-renders the gallery on a variant change and the new hero's
        // `load` lands here. Until now this re-measured the box and re-recorded
        // the lines but never re-asked which variant is selected, so a colour
        // resolved before the form caught up stayed on the cover for good.
        // COLOUR ONLY — applyVariantVisual() can swap the hero, and swapping it
        // from the hero's own load handler is a loop.
        this.applyVariantColor();
        this.positionOverlay();
        this.fitText();
        this.updateMini();
        // A box with no size says nothing about wrapping: the fit did not run
        // and the text was not re-flowed, so the descriptor still describes the
        // layout the customer last saw. Keep it. Recording here instead wrote
        // `lines: null` beside a stale size every time a hidden slide or a
        // decoding hero arrived, which is what a third of the orders since the
        // cutover carried. watchOverlaySize() re-fits and re-records the moment
        // the box is back.
        if (!this._fitOk) return;
        this.setNote(this._tooBig);
        // The box just changed size, so the browser has re-wrapped the text —
        // and until this line the descriptor still held the breaks from before.
        // That gap printed order 7673: a rotation, a keyboard dismissal, an
        // address bar collapsing or a slow cover finally decoding all arrive
        // here, long after the last keystroke, and every one of them re-flows
        // the words the customer is looking at. Re-capture, or the order
        // carries a layout nobody ever had.
        this.updateDescriptor(this.overlayText ? this.overlayText.textContent : '');
      }

      // --- Per-variant visual (the dynamic flow) -----------------------------

      // Swap the hero image (base cover while typing / original when empty) and
      // Read the currently selected variant straight from the add-to-cart form,
      // which Dawn always keeps up to date — more reliable than the pub/sub event.
      currentVariantId() {
        // A standalone instance carries its own variant; the PDP's form is
        // another product's.
        if (this.variantInput) {
          const mine = Number(this.variantInput.value);
          if (mine) return mine;
        }
        const el = document.querySelector('.product-variant-id') || document.querySelector('form [name="id"]');
        const v = el && el.value ? Number(el.value) : null;
        // The COLOUR's variant: the hidden sibling carries no artwork of its
        // own, and the descriptor must name the variant whose cover it is.
        return this.colourOf(v) || this.state.variantId;
      }

      // --- Custom Cover Price -------------------------------------------------
      //
      // The paid cover (snippets/live-preview.liquid, «Custom Cover Price»)
      // lives in a hidden sibling variant. This keeps the add-to-cart form on
      // the right one — the colour's own variant while the field is empty, its
      // sibling the moment there is text — and the page's price and label on
      // what that variant really costs.
      //
      // ⚠ NOT a click on Dawn's picker. A picker change fetches the section,
      // disables «Προσθήκη» until it answers (for good if it never does) and
      // calls media-gallery's setActiveMedia(), which smooth-scrolls the page to
      // the photo — under the finger of a customer typing a name. So the form's
      // `id` is set directly, and the hidden «Εξώφυλλο» radio is checked
      // without an event: Dawn's own colour switch posts every checked value,
      // so the colour the customer picks next arrives already as a sibling.

      colourOf(id) {
        const n = Number(id) || null;
        return (n && this.cover && this.cover.colourOf[n]) || n;
      }

      coverIdInputs() {
        const formId = this.previewInput && this.previewInput.getAttribute('form');
        if (!formId) return [];
        const out = [];
        // the product form and, as Dawn keeps it, the installments form
        [formId, formId.replace(/^product-form-/, 'product-form-installment-')].forEach((id) => {
          const f = document.getElementById(id);
          const el = f && f.querySelector('input[name="id"]');
          if (el && out.indexOf(el) === -1) out.push(el);
        });
        return out;
      }

      syncCover(why) {
        const c = this.cover;
        if (!c) return;
        const inputs = this.coverIdInputs();
        if (!inputs.length) return;
        const cur = Number(inputs[0].value) || null;
        const colour = this.colourOf(cur);
        const pair = colour ? c.pairs[colour] : null;
        if (!pair) {
          // A colour without a sibling (the sync has not reached it): free,
          // and the label says so.
          this.showCoverPrice(null, false);
          return;
        }
        const hasText = !!(this.input && (this.input.value || '').trim());
        const custom = Number(pair.custom);
        const want = hasText ? custom : colour;
        if (want !== cur) {
          inputs.forEach((el) => {
            el.value = String(want);
            el.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
        this.checkCoverOption(pair, want === custom);
        this.showCoverPrice(pair, want === custom);
        this._coverLast = { why: why || '', variant: want, paid: want === custom };
      }

      preparePickedColour(e) {
        const t = e && e.target;
        if (!t || !t.closest || !t.closest('variant-selects') || t.closest('[data-lp-cover-option]')) return;
        const c = this.cover;
        const value = t.tagName === 'SELECT' ? t.value : (t.checked ? t.value : null);
        if (value == null) return;
        let pair = null;
        Object.keys(c.pairs).forEach((k) => { if (c.pairs[k].colour === value) pair = c.pairs[k]; });
        const any = c.pairs[Object.keys(c.pairs)[0]];
        const hasText = !!(this.input && (this.input.value || '').trim());
        // with a sibling: the text decides; without one: the colour's own variant
        this.checkCoverOption(pair || any, !!(pair && hasText));
      }

      checkCoverOption(pair, custom) {
        const value = custom ? pair.customValue : pair.classicValue;
        if (value == null) return;
        const root = this.closest('product-info') || document;
        const row = root.querySelector('[data-lp-cover-option]');
        if (!row) return;
        row.querySelectorAll('input[type="radio"]').forEach((r) => {
          r.checked = r.value === value;
        });
        // a dropdown picker: Dawn reads the `selected` ATTRIBUTE, not the property
        row.querySelectorAll('select option').forEach((o) => {
          const on = o.value === value;
          o.selected = on;
          if (on) o.setAttribute('selected', 'selected');
          else o.removeAttribute('selected');
        });
      }

      showCoverPrice(pair, custom) {
        const c = this.cover;
        const label = this.querySelector('[data-lp-label]');
        const want = pair && pair.diff ? String(c.labelPaid || '').split('[diff]').join(pair.diff) : c.label;
        if (label && want && label.textContent !== want) label.textContent = want;
        // what a screen reader announces for the field says the same as the label
        if (this.input && want && this.input.getAttribute('aria-label') !== want.trim()) {
          this.input.setAttribute('aria-label', want.trim());
        }
        if (!pair) return;
        const money = custom ? pair.customMoney : pair.classicMoney;
        if (!money) return;
        // The template's price («moodbook-custom-price», custom liquid) and the
        // phone's sticky bar (moved to <body>). Neither follows a variant on
        // its own: every variant of these books used to cost the same.
        document.querySelectorAll('#moodbook-custom-price, .mb-sticky-price[data-price]').forEach((el) => {
          if (el.textContent.trim() !== money) el.textContent = money;
        });
      }

      // resolve the text colour for the current variant.
      // The variant's text colour, on its own, so the paths that must not touch
      // the hero image can still keep the colour honest. See reflow().
      applyVariantColor() {
        this.state.variantId = this.currentVariantId();
        const v = this.variants[this.state.variantId] || {};
        const want = this.userColor || v.color || this.defaultColor;
        this.state.font.color = want;
        if (this.overlayText) {
          this.overlayText.style.setProperty('color', want || '#222', 'important');
        }
        if (this.miniText) {
          this.miniText.style.setProperty('color', want || '#222', 'important');
        }
        return v;
      }

      applyVariantVisual() {
        const v = this.applyVariantColor();

        const hasText = (this.state.text || '').trim().length > 0;
        const base = v.base || this.config.baseImage; // per-variant, else single blank cover
        this._variantBase = base || null; // remember for the descriptor (cart re-render)

        if (!this.heroImg) return;
        const original = v.original || this.heroOriginalSrc;
        const target = hasText && base ? base : original;
        if (target && this.heroImg.getAttribute('src') !== target) {
          this.swapHero(target);
        } else if (!target && this.heroOriginalSrcset) {
          this.heroImg.setAttribute('srcset', this.heroOriginalSrcset);
        }
      }

      // --- Sticky mini preview (mobile) ---------------------------------------

      // On a phone the cover sits well above the field, so the customer types
      // blind and has to scroll up to check. Pin a small live copy in the corner
      // while there is text and the real preview is off screen. It is drawn from
      // the SAME state and the same box%%, so it cannot disagree with what prints.
      isNarrow() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      ensureMini() {
        if (this.mini) return;
        const wrap = document.createElement('div');
        wrap.className = 'lp-mini';
        wrap.hidden = true;
        wrap.setAttribute('aria-hidden', 'true');
        const inner = document.createElement('div');
        inner.className = 'lp-mini-inner';
        const zoom = document.createElement('div');
        zoom.className = 'lp-mini-zoom';
        const img = document.createElement('img');
        img.className = 'lp-mini-base';
        img.alt = '';
        const ov = document.createElement('div');
        ov.className = 'lp-mini-overlay';
        const tx = document.createElement('div');
        tx.className = 'lp-mini-text';
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'lp-mini-close';
        close.setAttribute('aria-label', 'Απόκρυψη προεπισκόπησης');
        close.textContent = '×';
        ov.appendChild(tx);
        zoom.appendChild(img);
        zoom.appendChild(ov);
        inner.appendChild(zoom);
        wrap.appendChild(inner);
        wrap.appendChild(close);
        // On <body>: `position: fixed` breaks inside a transformed ancestor.
        document.body.appendChild(wrap);
        this.mini = wrap;
        this.miniInner = inner;
        this.miniZoom = zoom;
        this.miniImg = img;
        this.miniOverlay = ov;
        this.miniText = tx;

        // The copy re-fits once it knows its own size.
        img.addEventListener('load', () => this.updateMini());
        // Tapping it takes you to the real thing.
        inner.addEventListener('click', () => {
          const g = document.querySelector(this.config.heroSelector || 'media-gallery');
          if (g) g.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        close.addEventListener('click', (e) => {
          e.stopPropagation();
          this._miniDismissed = true;
          this.updateMini();
        });

      }

      // Watch the page for the moment the copy becomes useful. Wired once, from
      // updateMini(), so it is running long before the copy itself exists.
      watchMini() {
        if (this._miniWatching) return;
        this._miniWatching = true;

        // Follow the page: sit under the header wherever it is, and stand down as
        // soon as the real preview is on screen. Deliberately NOT an
        // IntersectionObserver and NOT rAF-throttled — both are frame callbacks and
        // stop being delivered whenever the document is not being painted (a
        // backgrounded tab, an embedded webview). Two getBoundingClientRect calls
        // on a time-throttled scroll handler cost nothing and always run.
        this._onMiniScroll = () => {
          const now = Date.now();
          if (now - (this._miniLast || 0) < 60) {
            clearTimeout(this._miniTail);
            this._miniTail = setTimeout(() => this._onMiniScroll(), 70);
            return;
          }
          this._miniLast = now;
          this.updateMini();
        };
        window.addEventListener('scroll', this._onMiniScroll, { passive: true });
        // The keyboard opening is a visualViewport event, not a scroll or a resize.
        if (window.visualViewport) {
          this._onViewport = () => this.updateMini();
          window.visualViewport.addEventListener('resize', this._onViewport);
          window.visualViewport.addEventListener('scroll', this._onViewport);
        }
        // Dawn locks the body while the cart drawer or the menu is open (see
        // cart-drawer.js / details-modal.js) — do not float over them.
        if (window.MutationObserver) {
          this._bodyObserver = new MutationObserver(() => this.updateMini());
          this._bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
          // The add-to-cart notification is the one overlay that does NOT lock
          // the body, so the guard above never sees it — and it is the one this
          // card is guaranteed to cover. Dawn gives it `z-index: -1`
          // (component-cart-notification.css) so it can slide out from BEHIND
          // the header, which means every positioned element on the page paints
          // over it, and this card is `z-index: 2`. Reported from a real phone
          // on 2026-08-24: the customer's own dedication lying across the
          // "added to your cart" panel, over the product name and the options.
          // Watch its class directly; nothing on <body> changes when it opens.
          var notif = document.getElementById('cart-notification');
          if (notif) this._bodyObserver.observe(notif, { attributes: true, attributeFilter: ['class'] });
        }
      }

      // Is Dawn's add-to-cart notification showing? `.active` is what
      // cart-notification.js adds on open and removes on close.
      cartNotificationOpen() {
        var n = document.getElementById('cart-notification');
        return !!(n && n.classList.contains('active'));
      }

      // Is the real preview close enough to fully on screen that a copy would be
      // noise? 90%% of the text box, which is the part the customer is checking.
      heroOnScreen() {
        const ov = this.overlay;
        if (!ov || !ov.isConnected) return false;
        const r = ov.getBoundingClientRect();
        if (!r.height) return false;
        const band = this.visibleBand();
        const seen = Math.min(r.bottom, band.bottom) - Math.max(r.top, band.top);
        return seen / r.height >= 0.9;
      }

      // What the customer can actually see right now, in layout-viewport
      // coordinates — the space getBoundingClientRect and `position: fixed` both
      // speak. With the keyboard up these are NOT the window: the keyboard shrinks
      // the visual viewport only.
      visibleBand() {
        const vv = window.visualViewport;
        const winH = window.innerHeight || document.documentElement.clientHeight;
        if (!vv) return { top: 0, bottom: winH, height: winH, keyboard: false };
        return {
          top: vv.offsetTop,
          bottom: vv.offsetTop + vv.height,
          height: vv.height,
          // A shrink of more than ~120px is a keyboard, not a browser toolbar.
          keyboard: vv.height < winH - 120,
        };
      }

      placeMini() {
        if (!this.mini || this.mini.hidden) return;
        const band = this.visibleBand();
        let top;
        if (band.keyboard) {
          // Keyboard up: hug the top of the visible band. Anchoring to the layout
          // viewport is exactly what pushed the card off screen — it is why the
          // preview only appeared once the keyboard was dismissed.
          top = band.top + 8;
        } else {
          const h = document.querySelector('sticky-header, .section-header, #shopify-section-header');
          const bottom = h ? h.getBoundingClientRect().bottom : 0;
          top = band.top + Math.max(10, bottom + 10);
        }
        this.mini.style.top = Math.round(top) + 'px';
        // Never taller than the band it has to fit in.
        this.mini.style.maxHeight = Math.max(90, band.height - 16) + 'px';
      }

      updateMini() {
        if (!this.stickyMobile) return;
        // Start listening BEFORE deciding to show anything. The scroll and
        // visualViewport handlers used to be wired inside ensureMini(), which only
        // runs once the copy is already wanted — and it is only wanted once the
        // real preview has scrolled away. Nothing was listening for the scroll
        // that would have asked, so the copy appeared only when some unrelated
        // resize happened to call in (a phone collapsing its address bar). On a
        // viewport that does not do that, the feature simply never ran.
        this.watchMini();
        const wanted =
          this.activated &&
          !this._miniDismissed &&
          this.isNarrow() &&
          (this.state.text || '').trim().length > 0 &&
          !this.heroOnScreen() &&
          !document.body.classList.contains('overflow-hidden') &&
          !this.cartNotificationOpen();
        if (!wanted) {
          if (this.mini) this.mini.hidden = true;
          return;
        }
        this.ensureMini();
        const base = this._variantBase || this.config.baseImage;
        if (base && this.miniImg.getAttribute('src') !== base) this.miniImg.setAttribute('src', base);
        // Never a 0-height sliver: wait for the artwork, same rule as the hero.
        if (!this.miniImg.complete || !this.miniImg.naturalWidth) {
          this.mini.hidden = true;
          return;
        }
        this.mini.hidden = false;
        this.placeMini();
        this.paintMini();
      }

      paintMini() {
        if (!this.mini || this.mini.hidden) return;
        const font = this.state.font;
        let text = this.state.text || '';
        if (font.transform === 'uppercase') text = text.toLocaleUpperCase('el-GR');
        const el = this.miniText;
        el.textContent = text;
        el.style.color = font.color || '#222';
        el.style.setProperty('font-family', font.family ? `"${font.family}", serif` : 'inherit', 'important');
        el.style.lineHeight = font.leading || 1.15;
        el.style.fontWeight = font.weight || 700;
        el.style.textAlign = font.align || 'center';
        // A faithful thumbnail of the whole scene puts the text at ~7px: correct
        // and unreadable. The cover is only part of a square photo and the box is a
        // fraction of the cover, so the copy zooms to the text box plus a margin of
        // context. Geometry only — the same box%%, seen closer.
        const img = this.miniImg;
        if (!img.naturalWidth || !img.naturalHeight) return;
        const box = this.config.box || { x: 10, y: 40, w: 80, h: 15 };
        const pctNow = this._effSizePct != null ? this._effSizePct : font.sizePct || 80;
        const ratio = img.naturalHeight / img.naturalWidth;
        let cardW = this.mini.clientWidth;

        // Crop so the text box always fills the same share of the card, whatever
        // its shape: a fraction-of-the-box margin gives 27px text on one product
        // and 7px on another, because the boxes differ by 4x.
        //
        // That normalised the BOX against the card, which is not the thing anyone
        // reads. The text is a %% of the box HEIGHT, the card is sized by WIDTH, so
        // a flat box makes a short card and tiny letters: measured across the
        // catalogue the copy still ran 8px on "Πες μου" against 25px on the
        // wedding book. Below MIN_TEXT_PX the crop therefore tightens until the
        // letters reach it — bounded by FILL_MAX so the box itself never gets cut,
        // and one-directional, so nothing that already reads well shrinks.
        const FILL = 0.8;
        const FILL_MAX = 0.96;
        const MIN_TEXT_PX = 15;

        // A very flat box runs out of crop before it reaches the floor: cropping
        // tighter than the box would cut the customer's own text off. The card is
        // then allowed to grow WIDER instead — it is a short strip, so the extra
        // width costs little screen and buys proportional letters. Only ever to
        // rescue the floor, and released again the moment it is not needed, so the
        // stylesheet stays in charge for every product that reads fine.
        // Decide from the card's NATURAL width — never from one this function
        // applied last time, or the decision is not idempotent and the card
        // changes size on every repaint. It did: the rescue is clamped to `cap`,
        // so a card that needs more than the cap lands exactly ON it, `cardW <
        // cap` turns false, the release branch fires, and the next pass widens
        // it again. Measured on the live PDP of «Πες μου Παππού» at 375px with a
        // 137-character dedication: 165 → 203 → 165 → 203, a 38px swing on every
        // keystroke and every scroll tick, in the corner of the customer's eye.
        // A shorter dedication converges instead of flipping, which is why it
        // looked intermittent rather than broken.
        if (this.mini.style.width) {
          this.mini.style.maxWidth = '';
          this.mini.style.width = '';
          cardW = this.mini.clientWidth;
        }
        const cap = Math.min((54 / 100) * window.innerWidth, 240);
        const bestHere = (box.h * cardW * ratio * pctNow * FILL_MAX) / (box.w * 100);
        if (bestHere > 0 && bestHere < MIN_TEXT_PX && cardW < cap) {
          // Whole pixels: clientWidth is an integer, so a fractional width makes
          // the next pass measure something slightly different from what it set.
          const want = Math.round(Math.min(cap, cardW * (MIN_TEXT_PX / bestHere))) + 'px';
          // max-width owns the card in the stylesheet, so widening has to lift it
          // too — set width alone and the rescue is silently capped back.
          this.mini.style.maxWidth = want;
          this.mini.style.width = want;
          cardW = this.mini.clientWidth;
        }

        let fill = FILL;
        const cwForMin = (box.h * cardW * ratio * pctNow) / (MIN_TEXT_PX * 100);
        if (cwForMin > 0 && cwForMin < box.w / fill) {
          fill = Math.min(FILL_MAX, box.w / cwForMin);
        }
        const cw = Math.min(100, box.w / fill);
        const ch = Math.min(100, box.h / fill);
        const cx = Math.min(Math.max(0, box.x + box.w / 2 - cw / 2), 100 - cw);
        const cy = Math.min(Math.max(0, box.y + box.h / 2 - ch / 2), 100 - ch);
        const outerW = cardW;
        if (!outerW || !cw || !ch) return;
        const imgW = outerW / (cw / 100);
        const imgH = imgW * (img.naturalHeight / img.naturalWidth);
        this.miniZoom.style.width = imgW + 'px';
        this.miniZoom.style.left = -((cx / 100) * imgW) + 'px';
        this.miniZoom.style.top = -((cy / 100) * imgH) + 'px';
        this.miniInner.style.height = (ch / 100) * imgH + 'px';
        this.placeBox(img, this.miniOverlay);
        // sizePct is %% of the box height and the copy's box is a scaled twin of the
        // hero's — the same number reproduces the same picture at the copy's scale.
        const pct = this._effSizePct != null ? this._effSizePct : font.sizePct || 80;
        el.style.fontSize = (this.miniOverlay.clientHeight * pct) / 100 + 'px';
      }

      // --- Atomic hero swap --------------------------------------------------

      // A variant switch re-draws the text at once (it is only DOM) while the new
      // cover is still on the wire, so the customer saw their words hanging over a
      // frame that did not belong to them. Decode the artwork off-screen first,
      // hold the overlay hidden while it is in flight, and swap image + text in the
      // same frame. Covers are pre-warmed on focus, so this is normally a cache hit
      // and the swap is synchronous.
      swapHero(url) {
        const img = this.heroImg;
        const pre = LivePreview.preload(url);
        const current = () => this.heroImg === img && this._pendingHero === url;
        const reveal = () => {
          if (this._revealTimer) clearTimeout(this._revealTimer);
          this._revealTimer = null;
          this.setOverlayVisible(true);
          this.reflow();
        };
        // Put the decoded artwork on the visible element, and only give the text
        // back once THAT element has the frame too — an <img> keeps painting the
        // previous picture until the new one is ready, cache hit or not.
        const paint = () => {
          if (!current()) return;
          this._pendingHero = null;
          img.setAttribute('srcset', '');
          img.setAttribute('src', url);
          if (img.complete && img.naturalWidth) {
            reveal();
            return;
          }
          this.hideOverlay();
          (img.decode ? img.decode() : Promise.reject()).then(reveal).catch(() => {
            img.addEventListener('load', reveal, { once: true });
            img.addEventListener('error', reveal, { once: true });
          });
        };
        this._pendingHero = url;
        if (pre.complete && pre.naturalWidth) {
          paint();
          return;
        }
        this.hideOverlay();
        (pre.decode ? pre.decode() : Promise.reject()).then(paint).catch(() => {
          if (pre.complete && pre.naturalWidth) {
            paint();
            return;
          }
          pre.addEventListener('load', paint, { once: true });
          // A cover that never loads must not leave the text invisible.
          pre.addEventListener(
            'error',
            () => {
              if (!current()) return;
              this._pendingHero = null;
              reveal();
            },
            { once: true }
          );
        });
      }

      // Hidden, never stuck: whatever goes wrong upstream, the text comes back.
      hideOverlay() {
        this.setOverlayVisible(false);
        if (this._revealTimer) clearTimeout(this._revealTimer);
        this._revealTimer = setTimeout(() => {
          this._revealTimer = null;
          this.setOverlayVisible(true);
          this.reflow();
        }, 2000);
      }

      setOverlayVisible(on) {
        if (this.overlay) this.overlay.style.visibility = on ? '' : 'hidden';
      }

      // Warm every variant's blank cover the moment the customer touches the
      // field, so changing colour later is a cache hit rather than a round trip.
      //
      // The cover about to be shown goes FIRST and alone; the other colours
      // follow once it has landed. Firing all six at once shared a slow phone's
      // bandwidth six ways at the exact moment one of them was needed, and the
      // text sat on the generic photo past the 8-second audit — the
      // `artwork-mismatch` reports on our_wedding, both on its default colour.
      warmBases() {
        if (this._warmed) return;
        this._warmed = true;
        const cur = this.variants[this.currentVariantId()];
        const first = (cur && cur.base) || this.config.baseImage || null;
        const rest = [];
        const add = (u) => { if (u && u !== first && rest.indexOf(u) === -1) rest.push(u); };
        add(this.config.baseImage);
        Object.keys(this.variants).forEach((k) => add(this.variants[k] && this.variants[k].base));
        const warmRest = () => rest.forEach((u) => LivePreview.preload(u));
        if (!first) { warmRest(); return; }
        const im = LivePreview.preload(first);
        if (im.complete) { warmRest(); return; }
        let done = false;
        const go = () => { if (!done) { done = true; warmRest(); } };
        im.addEventListener('load', go, { once: true });
        im.addEventListener('error', go, { once: true });
        setTimeout(go, 4000); // a cover that never answers must not hold the rest
      }

      static preload(url) {
        if (!LivePreview._preloads) LivePreview._preloads = new Map();
        let im = LivePreview._preloads.get(url);
        if (!im) {
          im = new Image();
          im.decoding = 'async';
          im.src = url;
          LivePreview._preloads.set(url, im);
        }
        return im;
      }

      // --- Rendering ---------------------------------------------------------

      render() {
        // The cover first: a slide that Dawn has hidden has no box to draw in.
        // Only on the customer's own action — see renderForUser().
        if (this._userAction && this.showCoverSlide()) this.positionOverlay();
        const wasTooBig = !!this._tooBig;
        this.applyVariantVisual();

        const font = this.state.font;
        let text = this.state.text || '';
        if (font.transform === 'uppercase') text = text.toLocaleUpperCase('el-GR');

        const el = this.overlayText;
        el.textContent = text;
        // !important for the same reason the font-family below carries it: a
        // theme rule on media/gallery descendants beats a plain inline value,
        // and the descriptor records `font.color` either way — so the cover the
        // customer sees would quietly stop being the cover we print. Order
        // #7710 shipped with the box painted in ANOTHER variant's colour
        // (#606161) while the order said #d9c7c5, and nothing downstream could
        // see it because only the family was ever recorded as resolved.
        el.style.setProperty('color', font.color || '#222', 'important');
        // Use !important so a theme font-family rule (e.g. Dawn/Neutra applied with
        // !important to media/gallery descendants) can't override the chosen font —
        // an inline !important always beats an external !important. (Bug: fonts
        // "wouldn't change" on the storefront while the inline value was correct.)
        el.style.setProperty('font-family', font.family ? `"${font.family}", serif` : 'inherit', 'important');
        el.style.lineHeight = font.leading || 1.15; // per-font line spacing
        el.style.fontWeight = font.weight || 700;
        el.style.textAlign = font.align || 'center';

        this.fitText();
        if (this._fitOk) {
          if (this._tooBig) {
            if (this.refuseInput(wasTooBig)) return; // the field went back; that render already ran
          } else {
            this._acceptedText = this.state.text;
          }
          this.setNote(this._tooBig);
        }
        this.updateMini();
        this.updateDescriptor(text);
        // after the descriptor: it names the colour, never the sibling, so
        // switching the form here cannot make the capture stale
        if (this.cover) this.syncCover('render');
        this.writeDebug();
      }

      // The floor of the size the text may render at, as %% of the box height.
      //
      // Decided on 2026-08-20: no limit on lines and none on characters — both
      // ignore how big the box is, and the boxes differ by 4× across the
      // catalogue — the limit is on the RENDERED SIZE, per font. Each font may
      // carry `minSizePct` in custom.live_preview (the box-tool writes it);
      // where it does not, the floor is what would print at `minPt` points on
      // the real artwork (boxPt is in points), and never under the 4% the fit
      // itself bottoms out at. So «fifteen lines on a phone» is refused on
      // every product, while the smallest dedication anyone has actually
      // ordered — 7.9% of its box — is untouched.
      minSizePct() {
        const spec = this.fontSpecs[this.state.font.family] || {};
        let pct = Number(spec.minSizePct) || 0;
        const bh = this.config.boxPt && Number(this.config.boxPt.h);
        const minPt = Number(this.config.minPt) || 0;
        if (bh > 0 && minPt > 0) pct = Math.max(pct, (minPt / bh) * 100);
        return Math.max(pct, 4);
      }

      // A keystroke that pushes the text under the floor is not accepted: the
      // field goes back to the last text that fitted, and the note under it
      // says why. Only for typing — see onInput(). Returns true when it did.
      //
      // Three things it deliberately does NOT undo, found by review before
      // anyone typed them:
      //  - a keystroke into text that was ALREADY too big (a font change or a
      //    resize left it so): the customer is shortening it, and undoing every
      //    Backspace would lock them out of their own field;
      //  - a paste: the text arrives whole, so «go back to what fitted» means
      //    deleting all of it with only a note to say why. Kept, clamped to the
      //    floor, noted — they can cut it down;
      //  - the caret: after a refusal it goes back to where it was, not to the
      //    end of the dedication.
      refuseInput(wasTooBig) {
        if (!this._fromInput || wasTooBig) return false;
        const back = this._acceptedText;
        const now = this.state.text || '';
        if (back == null || back === now) return false;
        if (Math.abs(now.length - back.length) > 2) return false; // a paste, not a keystroke
        this._fromInput = false;
        this.input.value = back;
        this.state.text = back;
        const caret = this._caret;
        if (caret != null) {
          const at = Math.max(0, Math.min(back.length, caret - (now.length - back.length)));
          try { this.input.setSelectionRange(at, at); } catch (e) { /* not focused, or no such API */ }
        }
        this.setNote(true);
        this.renderForUser();
        this.setNote(true); // that render fitted, and would have cleared it
        return true;
      }

      setNote(on) {
        if (this.note) this.note.hidden = !on;
      }

      writeDebug() {
        if (!this.jsDebug) return;
        const vid = this.state.variantId;
        const keys = Object.keys(this.variants);
        const tail = (u) => (u ? String(u).split('/').pop() : 'none');
        this.jsDebug.textContent =
          'JS DEBUG\n' +
          'config.variants parsed: ' + (this.config && this.config.variants ? 'yes' : 'NO — JSON.parse failed') + '\n' +
          'variantId: ' + vid + ' (' + typeof vid + ')\n' +
          'variant keys: ' + keys.join(', ') + '\n' +
          'variant FOUND for this id: ' + !!this.variants[vid] + '\n' +
          'applied text colour: ' + this.state.font.color + '\n' +
          'config.baseImage: ' + tail(this.config.baseImage) + '\n' +
          'hero <img> src now: ' + tail(this.heroImg && this.heroImg.getAttribute('src'));
      }

      // Size the text to the box. All sizes are expressed as %% of the box
      // HEIGHT (sizePct), the same unit the print stamper (stamp.py) uses, so the
      // final rendered size travels in the descriptor and prints identically.
      //
      // 1) Find the largest sizePct whose wrapped text fits BOTH axes = the fill.
      //    - autofit on  : fill is only capped by the admin max.
      //    - autofit off : fill is capped by the configured font size (sizePct).
      // 2) Hybrid stepper: the customer's chosen size steps within [min, fill];
      //    untouched, it tracks the fill so the text always fills the box.
      fitText() {
        const box = this.overlay;
        const el = this.overlayText;
        const maxW = box.clientWidth;
        const maxH = box.clientHeight;
        // A box with no size cannot be fitted, and this is how order 7673 was
        // built: the fit bailed out here, `_effSizePct` kept the value from an
        // EARLIER fit, and updateDescriptor() went on to record line breaks from
        // a DOM that had never been fitted at all. That pairing is what made the
        // order arithmetically impossible — five lines at a size where five
        // lines cannot fit — and it is why the breaks looked like nothing any
        // browser would do: they were measured in a collapsed box. Say so, so
        // the capture can be refused rather than believed.
        this._fitOk = false;
        if (maxW <= 0 || maxH <= 0) return;

        const fits = (pct) => {
          el.style.fontSize = (maxH * pct) / 100 + 'px';
          return el.scrollHeight <= maxH + 1 && el.scrollWidth <= maxW + 1;
        };
        // The selected font's OWN sizePct is the target/ceiling (that's what it was
        // tuned to in the box-tool); the admin max is just an extra guard.
        const hiCap = Math.min(this.state.font.sizePct || 80, this.sizeMax);
        // …and the floor is the smallest size this text is allowed to print at.
        // Below it the search does not go: a text that does not fit AT the
        // floor is «too big for the cover», reported as such, and render()
        // decides what to do about it. It used to bottom out at 4% regardless,
        // which on a phone's 90px box drew 15 lines at 4.5px — the
        // `text-not-sized` reports on the school and travel planners.
        const floor = this.minSizePct();
        this._tooBig = !fits(floor);
        // Binary-search the largest fitting sizePct in [floor..hiCap].
        let lo = floor;
        let hi = Math.max(floor, hiCap);
        let fillPct = floor;
        for (let i = 0; i < 20; i++) {
          const mid = (lo + hi) / 2;
          if (fits(mid)) {
            fillPct = mid;
            lo = mid;
          } else {
            hi = mid;
          }
        }

        // The stepper's range is a FRACTION OF THE FILL, never an absolute %% of
        // the box. sizePct is a per-font ceiling tuned on the real artwork and runs
        // 11–49%% across the catalogue, so an absolute floor (the block ships 40)
        // sat above almost every fill: − was disabled because the text was already
        // "below the minimum", + because it was already at the fill. Both dead.
        // The one absolute it respects is the print floor above: «−» stops there.
        this._fillPct = fillPct;
        this._floorPct = Math.max((fillPct * this.sizeMin) / 100, floor);
        this._stepPct = Math.max((fillPct * this.sizeStep) / 100, 0.5);

        let effPct;
        if (!this.sizeTouched || this.userSizePct == null) {
          // Land BELOW the fill, not on it. The fill is capped by the font's own
          // sizePct, so a short line — two words over three lines — stops growing
          // only when it reaches that ceiling and lands at full size, which reads
          // as enormous against the artwork. sizeStart backs the untouched default
          // off the ceiling; + still climbs all the way up to the fill.
          effPct = Math.min(fillPct, (fillPct * this.sizeStart) / 100);
          effPct = Math.max(effPct, Math.min(this._floorPct, fillPct));
          this.userSizePct = effPct;
        } else {
          // Never exceed the fill (no overflow), never go under the floor.
          effPct = Math.min(this.userSizePct, fillPct);
          effPct = Math.max(effPct, Math.min(this._floorPct, fillPct));
        }
        el.style.fontSize = (maxH * effPct) / 100 + 'px';
        this._effSizePct = Math.round(effPct * 10) / 10;
        this._fitOk = true; // size and wrapping now describe the same layout

        // Reflect whether the stepper can still go up/down.
        if (this.sizeDec) this.sizeDec.disabled = effPct <= this._floorPct + 0.05;
        if (this.sizeInc) this.sizeInc.disabled = effPct >= fillPct - 0.05;

        this.applyInkBleed();
      }

      // How far outside its own line boxes does THIS text, in THIS face, at THIS
      // size, actually put ink?
      //
      // NOT from the face's declared metrics. That is why this stayed open, and
      // why two synthetic reproductions of it measured the wrong thing.
      // `ascender - descender` says Astir overruns its 1.15 leading by 0.20em;
      // Astir's real ink overruns by 0.005em — forty times less, on the very
      // face whose clipped `g` opened the question. It calls Cavafy safe
      // (declared 1.00 against a 1.15 leading) when Cavafy is the one face that
      // clips at the TOP, by 0.175em, on the Greek accents Ά Έ Ή Ί Ό Ύ Ώ. And it
      // names Memories the worst when the worst by a wide margin is Marilia:
      // 0.335em below the line and 0.67em to the LEFT, because its capital Ν
      // opens with a swash that starts well before the origin.
      //
      // A declared box is where the designer said glyphs MAY go. actualBounding-
      // Box* is where this string's glyphs DO go, and only that can be clipped.
      inkBleed() {
        const none = { top: 0, right: 0, bottom: 0, left: 0 };
        const el = this.overlayText;
        const box = this.overlay;
        if (!el || !box) return none;
        const text = el.textContent || '';
        if (!text.trim()) return none;

        const cs = getComputedStyle(el);
        const size = parseFloat(cs.fontSize) || 0;
        if (!size) return none;
        const lh = parseFloat(cs.lineHeight) || size;

        this._inkCtx = this._inkCtx || document.createElement('canvas').getContext('2d');
        const ctx = this._inkCtx;
        if (!ctx) return none;
        ctx.font = (cs.fontStyle || 'normal') + ' ' + (cs.fontWeight || '400') + ' ' +
                   size + 'px ' + cs.fontFamily;

        // Where the baseline sits inside a line box of height `lh`: the content
        // area (ascent + descent) is centred in it — that is half-leading.
        const probe = ctx.measureText('Hg');
        const fAsc = probe.fontBoundingBoxAscent;
        const fDesc = probe.fontBoundingBoxDescent;
        if (!isFinite(fAsc) || !isFinite(fDesc)) return none;  // no metrics, no bleed
        const baseline = (lh - (fAsc + fDesc)) / 2 + fAsc;

        // Vertical: ascent and descent belong to the glyph SET, so measuring the
        // whole string answers for every line at once.
        const all = ctx.measureText(text.replace(/\n/g, ' '));
        const top = all.actualBoundingBoxAscent - baseline;
        const bottom = baseline + all.actualBoundingBoxDescent - lh;

        // Horizontal: which glyph starts or ends a line depends on the wrap, so
        // ask every word — any of them can land first or last on its line.
        let left = 0;
        let right = 0;
        const words = text.split(/\s+/);
        for (let i = 0; i < words.length; i++) {
          if (!words[i]) continue;
          const m = ctx.measureText(words[i]);
          if (m.actualBoundingBoxLeft > left) left = m.actualBoundingBoxLeft;
          if (m.actualBoundingBoxRight - m.width > right) right = m.actualBoundingBoxRight - m.width;
        }

        // A bleed wider than half the box means the metrics are nonsense, or a
        // fallback face answered. Clamp rather than throw the layout across the
        // cover — half the box each way is already far past any real face.
        const capW = (box.clientWidth || size * 4) / 2;
        const capH = (box.clientHeight || size * 4) / 2;
        const clamp = (v, cap) => Math.min(Math.max(0, v || 0), cap);
        return {
          top: clamp(top, capH),
          bottom: clamp(bottom, capH),
          left: clamp(left, capW),
          right: clamp(right, capW)
        };
      }

      // Let the clip rectangle be slightly larger than the box, WITHOUT touching
      // the box.
      //
      // `clip-path: inset()` takes negative values, so the clip can sit outside
      // the border box. That matters more than it sounds: the first version of
      // this padded the overlay instead, and padding changes the content box —
      // so every caller that sets `overlay.style.width` (the harness probe does,
      // and so would anything measuring the box) silently got a narrower box,
      // the text re-wrapped, and «Our wedding» broke as «Our weddin / g». Four
      // assertions caught it. The recorded `lines` are the contract here; a fix
      // for a cosmetic clip must not be able to move them, and the only way to
      // guarantee that is to leave the geometry completely alone.
      //
      // overflow goes to `visible` because it would clip first. A browser too old
      // for clip-path therefore shows the text uncropped rather than cropped,
      // which is the better of the two failures.
      applyInkBleed() {
        const box = this.overlay;
        if (!box) return;

        let b = null;
        try { b = this.inkBleed(); } catch (e) { b = null; }
        if (!b || !(b.top || b.right || b.bottom || b.left)) {
          box.style.removeProperty('clip-path');
          box.style.removeProperty('overflow');
          return;
        }
        box.style.overflow = 'visible';
        box.style.clipPath = 'inset(' + (-b.top) + 'px ' + (-b.right) + 'px ' +
                             (-b.bottom) + 'px ' + (-b.left) + 'px)';
      }

      // Is this webfont GENUINELY usable, or are we about to measure a fallback?
      //
      // document.fonts.check() cannot answer this. It reports whether the text
      // CAN be rendered, and answers true for a family that was never declared,
      // because a fallback always can — verified: check('40px "Astir"') returns
      // true on a page with zero registered faces. Ask the registry instead.
      //
      // Unknown means NO here, the opposite of the health monitor's default: a
      // wrong answer there is a false alarm, a wrong answer here is a printed
      // book with the wrong line breaks.
      faceLoaded(family) {
        if (!family || !document.fonts || !document.fonts.forEach) return false;
        let found = false;
        try {
          document.fonts.forEach((f) => {
            if (f.family === family && f.status === 'loaded') found = true;
          });
        } catch (e) {
          return false;
        }
        return found;
      }

      // Where the BROWSER actually broke the text into visual lines.
      //
      // This is the one renderer the customer sees, so its line breaks are the
      // truth. Recording them means print and the mockup never have to decide
      // for themselves — PyMuPDF and Pillow measure differently and were
      // producing different break points from the same string (5 lines vs 6 on
      // a real dedication). Walk the text node a character at a time; a change
      // in the character's top edge is a wrap.
      capturedLines() {
        const el = this.overlayText;
        const node = el && el.firstChild;
        this._linesWhy = null;
        if (!node || node.nodeType !== 3) { this._linesWhy = 'nonode'; return null; }
        const s = node.textContent;
        if (!s) { this._linesWhy = 'empty'; return null; }
        const range = document.createRange();
        const lines = [];
        const wraps = []; // offsets where the BROWSER wrapped (not typed newlines)
        let start = 0;
        let prevTop = null;
        // The numbers the refusal was made on. Every iPhone order that lost its
        // transcript since 22/09 says «incredible», and no engine on this
        // machine reproduces it with the same text, box and face — so the next
        // one has to arrive carrying its own measurements. Numbers only: how
        // far the per-character top moved, against a line box that says what a
        // REAL wrap would have measured.
        let maxJump = 0;
        this._linesInfo = null;
        for (let i = 0; i < s.length; i++) {
          if (s[i] === '\n') {
            lines.push(s.slice(start, i));
            start = i + 1;
            prevTop = null;
            continue;
          }
          range.setStart(node, i);
          range.setEnd(node, i + 1);
          const rect = range.getClientRects()[0];
          if (!rect) continue;
          if (prevTop !== null) {
            const jump = Math.abs(rect.top - prevTop);
            if (jump > maxJump) maxJump = jump;
            if (jump > 1) {
              lines.push(s.slice(start, i).replace(/\s+$/, ''));
              wraps.push(i);
              start = i;
            }
          }
          prevTop = rect.top;
        }
        lines.push(s.slice(start));
        if (this.wrapsAreCredible(node, wraps)) return lines;
        this._linesWhy = 'incredible';
        if (this._linesInfo) {
          this._linesInfo.wraps = wraps.length;
          this._linesInfo.jump = Math.round(maxJump * 10) / 10;
          this._linesInfo.box = Math.round(this.lineBoxPx() * 10) / 10;
        }
        return null;
      }

      // One line box, in px — what a REAL wrap moves the top by. The yardstick
      // the jump above has to be read against.
      lineBoxPx() {
        try {
          const cs = getComputedStyle(this.overlayText);
          const lh = parseFloat(cs.lineHeight);
          if (lh > 0) return lh;
          return parseFloat(cs.fontSize) * (Number(this.state.font.leading) || 1.15);
        } catch (e) { return 0; }
      }

      // Does the capture describe a layout that could actually have happened?
      //
      // Recording nothing is safe — every renderer then wraps for itself, which
      // is what they all did before `lines` existed. Recording a WRONG break is
      // not: print and the cart copy it verbatim, and that is how order 7673
      // went out as `Our w` / `edding”` on a cover wide enough for the whole
      // phrase. So exactly one thing is refused — a word split in two when the
      // whole word would have fitted a line of its own. Nothing a browser does
      // legitimately looks like that. A break inside a word that genuinely
      // cannot fit is kept: there the break is the only honest answer, and a
      // renderer left to itself would have to make the same one.
      wrapsAreCredible(node, wraps) {
        if (!wraps.length) return true;
        const s = node.textContent;
        const avail = this.overlayText.clientWidth;
        if (!(avail > 0)) return true; // nothing to measure against; do not accuse
        const space = (c) => c === undefined || /\s/.test(c);
        // A break after one of these is legitimate with no space around it —
        // the browser offers the opportunity and a re-wrap would take it too.
        const breakAfter = (c) => c !== undefined && /[-\u2010-\u2014\u002f\u005c]/.test(c);
        const range = document.createRange();
        for (let n = 0; n < wraps.length; n++) {
          const i = wraps[n];
          if (space(s[i - 1]) || space(s[i])) continue; // broke at a space: normal
          if (breakAfter(s[i - 1])) continue; // broke at a dash or a slash: normal
          let a = i;
          while (a > 0 && !space(s[a - 1])) a--;
          let b = i;
          while (b < s.length && !space(s[b])) b++;
          range.setStart(node, a);
          range.setEnd(node, b);
          // The word sits on two lines, so it comes back as two rects; their
          // widths sum to what it would occupy on one.
          const rects = range.getClientRects();
          let w = 0;
          for (let k = 0; k < rects.length; k++) w += rects[k].width;
          if (w <= avail + 0.5) {
            // The two widths that made it a refusal: the word as measured, and
            // the line it is said to have fitted. If these two are close, the
            // break was a borderline one; if the word is far smaller, the
            // «wrap» the walk saw was not a wrap at all.
            this._linesInfo = { word: Math.round(w), avail: Math.round(avail) };
            return false;
          }
        }
        return true;
      }

      updateDescriptor(renderedText) {
        if (!this.previewInput) return;
        const box = this.config.box || {};
        const font = this.state.font;
        const descriptor = {
          v: 1,
          product: this.config.product || null,
          variantId: this.state.variantId,
          // Self-contained web-preview extras (ignored by the print stamper):
          // the base cover the text sits on, so the cart page can re-render it.
          base: this._variantBase || this.config.baseImage || null,
          // …and what the browser ACTUALLY put on screen, beside what it was
          // told to. The worst failure in this feature renders perfectly: when
          // the personalised cover 404s the hero keeps showing the GALLERY
          // photo, every health check passes, and the customer's name goes to
          // print on generic artwork. `renderedBase` is the only record of that
          // — it is `currentSrc`, so it also resolves whichever srcset entry the
          // device chose. Nothing downstream renders from it; it is evidence.
          renderedBase: (this.heroImg && this.heroImg.currentSrc) || null,
          layers: [
            {
              id: this.config.layerId || 'title',
              content: renderedText,
              // The visual lines the customer actually saw. Print and the
              // mockup draw THESE verbatim instead of re-wrapping, so all
              // three renderers agree by construction rather than by luck.
              // Only trustworthy once the chosen webfont has loaded — a fit
              // against the fallback face would record the wrong breaks.
              // Only when the fit actually ran (so `sizePct` below and these
              // breaks describe ONE layout) and the real face is loaded (so the
              // breaks were not measured against a fallback).
              lines: this._fitOk && this.faceLoaded(font.family) ? this.capturedLines() : null,
              // WHY there are none, when there are none — one word from a fixed
              // list, never the text. 60 of 153 orders since the cutover carry
              // `lines: null`, every one of them from iOS (GA4 funnel join,
              // 22/09: 9 of 9 iOS, 0 of 9 elsewhere), and neither headless
              // Chrome nor Playwright's WebKit reproduces it. The next iOS order
              // says which gate refused, instead of another week of guessing.
              linesWhy: this.linesWhy(font.family),
              // …and, when the refusal was the credibility check, the five
              // numbers it was made on. No text, ever — widths in px, a count,
              // and the line box they are read against.
              linesInfo: this._linesWhy === 'incredible' ? this._linesInfo : null,
              multiline: true,
              // The text did not fit at the floor and is drawn clamped to it,
              // clipped by the box — a font change or a resize left it so, and
              // those are not undone (see refuseInput()). The print gate would
              // block such a cover anyway (the photograph is clipped, the print
              // is not); this says WHY, before anyone measures anything.
              tooBig: !!(this._fitOk && this._tooBig),
              box: { x: box.x, y: box.y, w: box.w, h: box.h },
              // Points source of truth (from the live_preview_layout metaobject),
              // so print stamps exactly; % above is for browser use only.
              boxPt: this.config.boxPt || null,
              artworkPt: this.config.artworkPt || null,
              font: {
                family: font.family || null,
                // The family the browser actually resolved, beside the one we
                // asked for. `lines` are only trustworthy if they were measured
                // against the real face; this says which face that was, so an
                // order can be checked after the fact instead of taken on trust.
                renderedFamily: this.overlayText ? getComputedStyle(this.overlayText).fontFamily || null : null,
                // …and the colour it resolved, for exactly the same reason. The
                // family was recorded and the colour was not, so a variant
                // switch that left the paint behind (#7710) was invisible in
                // the order and took a photograph and a hand measurement to
                // find. With this, `check_orders.py` sees it before printing.
                renderedColor: this.overlayText ? getComputedStyle(this.overlayText).color || null : null,
                // The font file URL, so the cart can @font-face it on its own.
                url: this.fontUrls[font.family] || null,
                // The size actually rendered (autofit fill or the customer's
                // stepped size), so print (stamp.py) reproduces it exactly.
                sizePct: this._effSizePct != null ? this._effSizePct : font.sizePct || 80,
                // Line spacing as a MULTIPLIER of the size, so it survives the
                // auto-shrink and scales identically in the cart and in print.
                leading: font.leading || 1.15,
                weight: font.weight || 700,
                color: font.color || null,
                align: font.align || 'center',
              },
              transform: font.transform || 'none',
            },
          ],
        };
        const has = renderedText.trim().length > 0;
        const json = has ? JSON.stringify(descriptor) : '';
        this.previewInput.value = json;
        if (this.previewBaseInput) this.previewBaseInput.value = has ? descriptor.base || '' : '';
        // Flag the item as personalised so the cart/checkout can hide COD etc.
        if (this.customInput) this.customInput.value = has ? 'Yes' : '';
        // A self-hosted viewer link (own domain) that re-renders this cover from
        // the descriptor — shows as a clickable link in the Shopify order admin so
        // the merchant can verify the personalisation before printing. No hosting.
        // `view=cover-preview` forces the alternate template even when nobody
        // assigned it to the Page in the admin — without it the Page falls back
        // to the default template and the merchant just sees an empty title.
        var viewerUrl = has
          ? window.location.origin + '/pages/cover-preview?view=cover-preview&d=' + this.encodeDescriptor(json)
          : '';
        if (this.linkInput) this.linkInput.value = viewerUrl;

        // The same URL again, for a property whose name does NOT start with `_`.
        //
        // Shopify hides underscore-prefixed properties by convention, so the
        // customer's last sight of their own cover is the cart — at checkout it
        // is gone. A visible property reaches the checkout and the order
        // confirmation.
        //
        // ⚠ NOTHING USES THIS YET, AND THE CASE THAT MOTIVATED IT DID NOT HOLD.
        // It was written for a customer who went from checkout back to the cart
        // three times on 2026-08-22 (abandoned checkout 67776515047756) and then
        // left, read as someone checking that what she typed had survived. The
        // gaps between those pages were 11.5s and 39s — too fast to have looked
        // at anything. A slow checkout fits the evidence better, and the reading
        // was withdrawn. Kept because the mechanism is still the right one for
        // the real capture (tools/cover-capture): when there is an actual image
        // URL to show, the element goes into live-preview.liquid in one line.
        // Do not restore the abandoned-cart rationale — it is not what happened.
        //
        // This element is OPTIONAL and this file does not create it: the input,
        // and above all its NAME, is customer-facing copy and belongs in
        // live-preview.liquid where Stelios can approve the wording. Nothing
        // happens until it exists.
        if (this.linkInputVisible === undefined) {
          this.linkInputVisible = this.querySelector('[data-lp-link-visible]') || null;
        }
        if (this.linkInputVisible) this.linkInputVisible.value = viewerUrl;

        this.scheduleCapture(json);
      }

      // ═══ Capturing the cover the customer actually approved ═══════════════
      //
      // Everything downstream still re-derives the cover from the descriptor.
      // The guards written for order 7673 catch a capture that is IMPLAUSIBLE —
      // a break splitting a word that would have fitted, a size paired with a
      // layout that never ran. They cannot catch one that is merely wrong in a
      // plausible way, and renderedBase/renderedFamily prove what was painted
      // without proving how it flowed. This records the flow.
      //
      // ⚠ IT MUST NEVER DELAY ADD-TO-CART. That premise was tested expensively
      // once already (328ab0d): the personalisation field is an ordinary form
      // field, so the order is complete and printable without any of this. An
      // upload that costs a sale is worth less than no upload at all. So the
      // capture runs on a debounce WHILE the customer is still deciding, and the
      // URL is already sitting in the form by the time they press Add. If it is
      // not there, nothing waits for it.

      captureUrl() {
        // A public endpoint, not a secret — it takes no credential by design
        // (see tools/cover-capture/README.md). Hard-coded rather than a theme
        // setting because the setting would have to be passed through
        // live-preview.liquid, and that file belongs to another workstream.
        return (window.LP_CAPTURE_URL ||
                'https://moodbook-cover-capture.stelios-d58.workers.dev/cover');
      }

      // The property that carries the image, created here rather than in Liquid
      // for the same reason. `_`-prefixed: this one is machinery, not something
      // the customer reads.
      imageInput() {
        if (this._imageInput !== undefined) return this._imageInput;
        var el = this.querySelector('[data-lp-image]');
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = 'properties[_lp_image]';
          el.setAttribute('data-lp-image', '');
          var formId = this.previewInput && this.previewInput.getAttribute('form');
          if (formId) el.setAttribute('form', formId);
          this.appendChild(el);
        }
        this._imageInput = el;
        return el;
      }

      scheduleCapture(descriptorJson) {
        if (!descriptorJson) { this.clearCapture(); return; }
        // The signature is the descriptor itself: if anything the customer can
        // see changed, the stored image is stale and must not travel.
        this._captureSig = descriptorJson;
        if (this._captureTimer) clearTimeout(this._captureTimer);
        // Long enough that typing a name does not upload once per keystroke —
        // the endpoint allows ten writes a minute and a customer experimenting
        // would burn that in seconds.
        this._captureTimer = setTimeout(function (self) {
          self._captureTimer = null;
          self.runCapture();
        }, 2500, this);
      }

      // Run the pending capture right now instead of waiting out the debounce.
      // Safe to call as often as you like: runCapture() returns immediately when
      // the current descriptor has already been uploaded.
      captureNow() {
        if (this._captureTimer) { clearTimeout(this._captureTimer); this._captureTimer = null; }
        if (!this._captureSig) return;
        try { this.runCapture(); } catch (e) { /* never break the page */ }
      }

      clearCapture() {
        if (this._captureTimer) { clearTimeout(this._captureTimer); this._captureTimer = null; }
        this._captureSig = null;
        this._capturedSig = null;
        try { this.imageInput().value = ''; } catch (e) { /* never break the page */ }
      }

      // ═══ A picture that is allowed to disagree with us ════════════════════
      //
      // Everything else downstream re-derives the cover from the descriptor, so
      // it can only ever agree with the descriptor. Order #7685 is what that
      // costs: the transcript held a line break that was arithmetically
      // impossible at the size recorded beside it — «ο γάμος μας» needed 5.18
      // of the box's 9.93 em — and the stored image agreed with it anyway,
      // because the image was drawn FROM it. A witness that cannot contradict
      // the accused is not a witness.
      //
      // <foreignObject> is one that can. The browser lays the text out itself,
      // wraps it itself and rasterises it with its own engine, from the text
      // and the computed CSS alone. It never sees `lines`. If our transcript is
      // wrong, this picture and that transcript part company, and something
      // downstream can finally see it.
      //
      // It is not a screenshot. No page can screenshot itself — if one could it
      // would photograph your other tabs — and the session-replay tools do not
      // either: Clarity reconstructs from captured HTML and says so, which is
      // why its own FAQ has to explain layouts coming out wrong. This is as
      // close as the platform allows, and closer than they get: it renders on
      // the customer's device, in their own face, at the moment they buy,
      // instead of being replayed somewhere else later.
      //
      // THE FACE MUST BE INLINED. A foreignObject cannot reach out for a font,
      // and a picture measured against a serif fallback is precisely the lie
      // this exists to catch.
      fontDataUrl(url) {
        if (!url) return Promise.resolve(null);
        this._fontData = this._fontData || {};
        if (this._fontData[url]) return this._fontData[url];
        // A ceiling on the fetch, not only on the raster. Without one a request
        // that never settles — a captive portal, a dead CDN edge — leaves this
        // promise pending forever, and every later capture awaits the same
        // cached pending promise. Nothing would break visibly; the shop would
        // simply stop getting pictures and no one would be told.
        var timed = new Promise(function (res) { setTimeout(function () { res(null); }, 5000); });
        var p = Promise.race([timed, fetch(url, { mode: 'cors', credentials: 'omit' })
          .then(function (r) { return r && r.ok ? r.arrayBuffer() : null; })])
          .then(function (buf) {
            if (!buf) return null;
            // btoa in 8k slices: apply() on a 250 kB array blows the arg limit.
            var u8 = new Uint8Array(buf), bin = '';
            for (var i = 0; i < u8.length; i += 8192) {
              bin += String.fromCharCode.apply(null, u8.subarray(i, i + 8192));
            }
            var mime = /\.otf(\?|$)/i.test(url) ? 'font/otf'
                     : /\.ttf(\?|$)/i.test(url) ? 'font/ttf'
                     : 'font/woff2';
            return 'data:' + mime + ';base64,' + btoa(bin);
          })
          .catch(function () { return null; });
        // Cache the SUCCESS, never the failure. Every terminal branch above
        // resolves null — the 5s ceiling, a non-ok response, the catch — and a
        // cached null is permanent for the session. One slow first fetch would
        // silently downgrade every later capture to the drawn fallback,
        // INCLUDING the one at add-to-cart, which is the only one that reaches
        // an order. Forget it instead, and the next capture tries again.
        var self = this;
        p = p.then(function (d) { if (!d) delete self._fontData[url]; return d; });
        this._fontData[url] = p;
        return p;
      }

      // Rasterise the live overlay. Resolves to an <img> the size of the box,
      // or null — null is not a failure to handle loudly, it just means this
      // capture falls back to the drawn-from-descriptor path and says so.
      paintOverlay() {
        var t = this.overlayText, ov = this.overlay;
        if (!t || !ov) return Promise.resolve(null);
        var text = t.textContent;
        if (!text || !text.trim()) return Promise.resolve(null);
        var W = Math.round(ov.clientWidth), H = Math.round(ov.clientHeight);
        if (!(W > 0 && H > 0)) return Promise.resolve(null);
        var font = this.state.font || {};
        return this.fontDataUrl(this.fontUrls[font.family]).then(function (data) {
          if (!data) return null;   // no real face, no honest picture
          var cs = getComputedStyle(t);
          var esc = function (v) {
            return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          };
          // The style is READ OFF the live node, never restated from config:
          // restating it is how the twin renderers drifted in the first place.
          var svg =
            '<svg xmlns="http://www.w3.org/2000/svg" width="' + W + '" height="' + H + '">' +
            '<defs><style>@font-face{font-family:"lpshot";src:url(' + data + ');}</style></defs>' +
            '<foreignObject x="0" y="0" width="' + W + '" height="' + H + '">' +
            '<div xmlns="http://www.w3.org/1999/xhtml" style="width:' + W + 'px;height:' + H +
            'px;display:flex;align-items:center;box-sizing:border-box;">' +
            '<div style="width:100%;margin:0;font-family:lpshot' +
            ';white-space:' + cs.whiteSpace +
            ';overflow-wrap:' + cs.overflowWrap +
            ';font-size:' + cs.fontSize +
            ';line-height:' + cs.lineHeight +
            ';font-weight:' + cs.fontWeight +
            ';text-align:' + cs.textAlign +
            ';text-transform:' + cs.textTransform +
            ';color:' + cs.color + '">' + esc(text) + '</div>' +
            '</div></foreignObject></svg>';
          return new Promise(function (res) {
            var img = new Image(), settled = false;
            var finish = function (v) {
              // WHAT THIS PICTURE IS OF, carried on the picture. `text` was read
              // off the live node at the top of this function; the snapshot the
              // upload travels with was taken from `state` before it. Those are
              // two different moments and nothing used to compare them — which
              // is how order #7735 shipped a photograph of «Ελένη κ,» attached
              // to a descriptor that said «Ελένη και Θάνος». The picture now
              // says what it drew, and sendCapture() refuses to publish one that
              // drew something else.
              // …and the COLOUR it was painted in, for the same reason: a
              // capture in the previous variant's colour (#7710) is a stale
              // witness too, and this is what lets print name it from the
              // order alone.
              if (v) { try { v._lpText = text; v._lpAt = Date.now(); v._lpColor = cs.color; } catch (e) {} }
              if (!settled) { settled = true; res(v); }
            };
            img.onload = function () {
              // `load` is not «drawable». Safari fires it for an SVG image
              // before the picture is rasterised, and a drawImage() straight
              // out of this handler paints NOTHING — order #7795, iPhone on
              // iOS 18.7, went out with the blank cover as its m=dom witness
              // (GA4 funnel 18:06–18:11; every other capture that day had ink).
              // decode() resolves when the image can actually be drawn; a
              // browser without it, or one that rejects, draws as before and
              // rasterHasInk() below still has the last word.
              if (typeof img.decode === 'function') {
                var go = function () { finish(img); };
                try { img.decode().then(go, go); } catch (e) { go(); }
              } else {
                finish(img);
              }
            };
            img.onerror = function () { finish(null); };
            // Safari has historically refused foreignObject rasters outright.
            // A ceiling here is what keeps that a silent downgrade rather than
            // a capture that never resolves and an image property never set.
            setTimeout(function () { finish(null); }, 4000);
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
          });
        }).catch(function () { return null; });
      }

      // Draw hero + text into a canvas at the artwork's natural size, using the
      // SAME placement maths as stamp.py. If this drifts from the stamper, the
      // image and the print disagree and we are back to two sources of truth —
      // which is the whole problem this is meant to end.
      captureCanvas(painted) {
        var img = this.heroImg;
        var cfg = this.config || {};
        if (!img || !img.naturalWidth || !img.naturalHeight) return null;

        var raw = this.previewInput && this.previewInput.value;
        if (!raw) return null;
        var d;
        try { d = JSON.parse(raw); } catch (e) { return null; }
        var layer = d && d.layers && d.layers[0];
        if (!layer || !layer.content) return null;

        var lines = layer.lines;
        var f = layer.font || {};
        // A rasterised overlay needs no transcript — it IS the layout, and an
        // order whose transcript came back null is exactly the one most worth
        // having a picture of. Only the drawn-from-descriptor path still
        // refuses, because without `lines` it has nothing to draw.
        if (!painted && (!Array.isArray(lines) || !lines.length)) return null;
        // Either way the real face must be on screen: a picture of the serif
        // fallback is a picture of something the customer never saw.
        if (f.family && !this.faceLoaded(f.family)) return null;

        var W = img.naturalWidth;
        var H = img.naturalHeight;
        var canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext('2d');
        if (!ctx) return null;
        try {
          ctx.drawImage(img, 0, 0, W, H);
        } catch (e) {
          return null;    // tainted or not decoded — the descriptor still prints
        }

        // Box in natural pixels — from the PERCENTAGES, never from boxPt.
        //
        // This canvas is the storefront photograph: `ctx.drawImage(img)` two
        // lines up put `heroImg` on it, and `box` % is what every renderer of
        // that photograph uses — the overlay, the mini, the cart, the viewer,
        // mockup_png.py. `boxPt`/`artworkPt` are points on the PRINT SPREAD,
        // 992.1 × 708.7pt = 350 × 250mm of front + spine + back, and stamp.py
        // is right to prefer them because the page it draws on IS that spread.
        //
        // This code preferred them too, "exactly as the stamper prefers them",
        // and scaled them by the photograph's natural size. The two rectangles
        // are bridged by `calib` (see box-tool.html), which is not in the
        // descriptor and never was, so the scaling had nothing to correct with:
        // measured across all 13 shipped product-configs it put the box at
        // 59–70% across the image where the percentages say 28–45%, at 0.66×
        // the width. Every line that exactly filled the real box was drawn
        // 1.4× wider than the box it landed in, and this is the one renderer
        // with neither a shrink loop nor a clip, so it painted the overflow
        // instead of hiding it. That is "the letters fall outside the box",
        // on 100% of captures, on every product, since c3315c2.
        //
        // Two frames, one bridge, and the bridge does not travel: hold both
        // here and the only safe rule is to use the frame that matches the
        // pixels under the brush.
        var box = layer.box || { x: 10, y: 40, w: 80, h: 15 };
        var x0 = (box.x / 100) * W, y0 = (box.y / 100) * H;
        var bw = (box.w / 100) * W, bh = (box.h / 100) * H;

        // The rasterised overlay covers exactly the box it was measured in, so
        // it scales into it with nothing recomputed and nothing to drift.
        if (painted) {
          try { ctx.drawImage(painted, x0, y0, bw, bh); } catch (e) { return null; }
          return canvas;
        }

        var leading = Number(f.leading) || 1.15;
        var size = Math.max(4, bh * ((Number(f.sizePct) || 80) / 100));
        var step = size * leading;
        var block = lines.length * step;
        var top = y0 + (bh - block) / 2;

        ctx.fillStyle = f.color || '#222222';
        ctx.textBaseline = 'alphabetic';
        ctx.font = (f.weight || 700) + ' ' + size + 'px "' + (f.family || 'serif') + '", serif';

        // Ascent/descent as fractions of the size, the same quantities the
        // stamper reads off the font file.
        var probe = ctx.measureText('Hg');
        var asc = (probe.fontBoundingBoxAscent || size * 0.8) / size;
        var dsc = -(probe.fontBoundingBoxDescent || size * 0.2) / size;

        var align = f.align || 'center';
        for (var n = 0; n < lines.length; n++) {
          var ln = lines[n];
          if (ln === '') continue;
          var lw = ctx.measureText(ln).width;
          var lx = align === 'left' ? x0
                 : align === 'right' ? x0 + bw - lw
                 : x0 + (bw - lw) / 2;
          var lineTop = top + n * step;
          var baseline = lineTop + (step - (asc - dsc) * size) / 2 + asc * size;
          ctx.fillText(ln, lx, baseline);
        }
        return canvas;
      }

      // Which gate stopped `lines` from being recorded, or null when it was.
      // Evaluated AFTER capturedLines() ran, so `_linesWhy` is this render's.
      linesWhy(family) {
        if (!this._fitOk) return 'nofit';
        if (!this.faceLoaded(family)) return 'noface';
        return this._linesWhy || null;
      }

      // The capture is the moment everything has settled: 2.5 s after the last
      // keystroke, with the face loaded — captureCanvas() insists on it. A
      // descriptor written earlier with `lines: null` because the face or the
      // box was not ready can be recorded properly NOW, before the picture is
      // taken of it. If the reason was not readiness, this changes nothing.
      refreshLinesIfMissing() {
        var raw = this.previewInput && this.previewInput.value;
        if (!raw) return;
        var d;
        try { d = JSON.parse(raw); } catch (e) { return; }
        var layer = d && d.layers && d.layers[0];
        if (!layer || Array.isArray(layer.lines)) return;
        var why = layer.linesWhy;
        if (why !== 'nofit' && why !== 'noface') return;
        var fam = this.state.font && this.state.font.family;
        if (!this._fitOk || !this.faceLoaded(fam)) return;
        try { this.updateDescriptor(this.overlayText ? this.overlayText.textContent : ''); } catch (e) {}
        // updateDescriptor() armed a fresh 2.5 s timer for the sig we are about
        // to send right now; without this it would upload the same one twice.
        if (this._captureTimer) { clearTimeout(this._captureTimer); this._captureTimer = null; }
      }

      runCapture() {
        var self = this;
        this.refreshLinesIfMissing();
        var sig = this._captureSig;
        if (!sig || sig === this._capturedSig) return;   // nothing new to send

        // Ask for the real picture first. It is allowed to fail — the drawn
        // fallback still corroborates the descriptor, it just cannot contradict
        // it, and `m=` below records which of the two this file is so nothing
        // downstream mistakes a copy of our own record for a witness.
        var paint;
        try { paint = this.paintOverlay(); } catch (e) { paint = Promise.resolve(null); }
        paint.then(function (painted) { self.sendCapture(sig, painted); },
                   function () { self.sendCapture(sig, null); });
      }

      // Does the raster carry any ink at all? Drawn small and read back.
      //
      // A <foreignObject> that rendered nothing is a fully transparent image
      // that still resolves, still fires `load`, and still carries `_lpText`
      // — every certification passes and the picture is of nothing. Order
      // #7795 (iPhone, iOS 18.7 Safari) published exactly that: the blank
      // Snow cover, pixel for pixel (max difference 1 of 255), certified as a
      // picture of «Ο γάμος μας». A raster with no ink is not a witness, and
      // print must not be handed one labelled m=dom.
      rasterHasInk(painted) {
        try {
          var c = document.createElement('canvas');
          c.width = 96; c.height = 96;
          var x = c.getContext('2d');
          if (!x) return true;
          x.drawImage(painted, 0, 0, 96, 96);
          var px = x.getImageData(0, 0, 96, 96).data;
          for (var i = 3; i < px.length; i += 4) if (px[i] > 16) return true;
          return false;
        } catch (e) { return true; }   // tainted or unreadable: cannot judge, do not accuse
      }

      // «rgb(137, 86, 51)» → «895633». getComputedStyle answers in rgb();
      // the descriptor and the print speak hex.
      hexOf(css) {
        var m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(String(css || ''));
        if (!m) return '';
        var h = function (n) { var s = Number(n).toString(16); return s.length < 2 ? '0' + s : s; };
        return h(m[1]) + h(m[2]) + h(m[3]);
      }

      // The text inside a snapshot, without trusting it to be well formed.
      sigText(sig) {
        try {
          var d = JSON.parse(sig);
          return ((d.layers || [])[0] || {}).content;
        } catch (e) { return undefined; }
      }

      sendCapture(sig, painted) {
        var self = this;
        if (this._captureSig !== sig) return;    // changed while we were painting
        // ⚠ AND THE PICTURE MUST BE OF THIS TEXT. The check above compares the
        // snapshot with the CURRENT state; this one compares it with what was
        // actually drawn. They are different questions and only the second one
        // catches a raster of an earlier moment — #7735 passed the first and
        // failed nothing, because nothing asked. Both sides here are
        // `overlayText.textContent`, so this is exact string equality and no
        // transform or whitespace rule can weaken it.
        if (painted && painted._lpText !== undefined) {
          var want = this.sigText(sig);
          if (want !== undefined && painted._lpText !== want) {
            // A picture of the wrong moment is worse than no picture: print
            // would trust it. Drop it and let the next cycle draw a true one.
            try { this.imageInput().value = ''; } catch (e) {}
            this._capturedSig = null;
            this.scheduleCapture(sig);
            return;
          }
        }
        // ⚠ AND IT MUST HAVE DRAWN SOMETHING. Once more from the top in case
        // the face or the colour was still settling; blank twice for the same
        // text, and the drawn fallback goes instead, labelled m=desc — which
        // the gate reads as «corroboration, not evidence». That is the truth
        // of it, and it is what #7795 should have carried.
        if (painted && !this.rasterHasInk(painted)) {
          if (this._blankRasterSig !== sig) {
            this._blankRasterSig = sig;
            this._capturedSig = null;
            this.scheduleCapture(sig);
            return;
          }
          painted = null;
        }
        var mode = painted ? 'dom' : 'desc';
        var canvas;
        try { canvas = this.captureCanvas(painted); } catch (e) { canvas = null; }
        if (!canvas || !canvas.toBlob) return;

        // JPEG, not PNG: the cover is a photograph and a 1400px PNG of one runs
        // past the endpoint's 1.5 MB ceiling, where the same image as JPEG lands
        // in the low hundreds of KB. This is corroboration of what was on
        // screen, not the print master — the press still stamps the vector PDF.
        canvas.toBlob(function (blob) {
          if (!blob || blob.size > 1400000) return;
          try {
            fetch(self.captureUrl(), {
              method: 'POST',
              headers: { 'Content-Type': blob.type || 'image/jpeg' },
              body: blob,
              // No credentials, no cookies: the endpoint authorises nothing and
              // must never be handed anything to authorise with.
              credentials: 'omit',
              mode: 'cors',
              keepalive: false,
            }).then(function (res) {
              return res.ok ? res.json() : null;
            }).then(function (out) {
              if (!out || !out.url) return;
              // Only publish it if the customer has not changed anything since:
              // a stale image is worse than none, because print would trust it.
              if (self._captureSig !== sig) return;
              self._capturedSig = sig;
              // Carry the descriptor on the URL so the address outlives the
              // file. The cover is deleted after 30 days and the confirmation
              // email is not; with `?v=` the worker serves the photograph while
              // it exists and re-draws it from this descriptor afterwards, so
              // one link works forever. It is a descriptor, never a
              // destination — the worker builds the fallback URL itself.
              var v = '';
              try { v = self.encodeDescriptor(sig); } catch (e) { v = ''; }
              // `m=dom` means the browser drew this and it can disagree with
              // the descriptor; `m=desc` means it was drawn FROM the descriptor
              // and agreeing with it proves nothing. The print gate needs to
              // know which it is holding.
              var base = v ? out.url + '?v=' + v : out.url;
              // `p=` is the SHAPE of what was drawn — characters and lines, never
              // the words, the same discipline the health monitor keeps. It lets
              // print_gate.py name a stale witness on an order already placed,
              // instead of reporting it as a layout disagreement and accusing a
              // cover that is correct. `t=` is when, for reading afterwards.
              var shape = '';
              if (painted && painted._lpText !== undefined) {
                shape = '&p=' + painted._lpText.length + '.' +
                        painted._lpText.split(String.fromCharCode(10)).length +
                        '&t=' + (painted._lpAt || 0);
                // `c=` is the painted colour as six hex digits, no «#».
                var hx = self.hexOf(painted._lpColor);
                if (hx) shape += '&c=' + hx;
              }
              self.imageInput().value = base + (v ? '&' : '?') + 'm=' + mode + shape;
            }).catch(function () { /* offline, blocked, down — the order still prints */ });
          } catch (e) { /* ditto */ }
        }, 'image/jpeg', 0.92);
      }

      // Called at add-to-cart. It does NOT wait for an upload — it only refuses
      // to let a stale one leave with the order.
      sealCapture() {
        if (this._captureSig && this._captureSig !== this._capturedSig) {
          try { this.imageInput().value = ''; } catch (e) { /* never break the page */ }
        }
      }

      // URL-safe base64 of the (UTF-8) descriptor JSON, for the viewer link.
      encodeDescriptor(json) {
        try {
          return btoa(unescape(encodeURIComponent(json)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        } catch (e) {
          return encodeURIComponent(json);
        }
      }
    }
  );
}
